/**
 * Mobile History — Positions (closed) | Deals | Orders (done only; pending in Trade dock)
 * embedded+mt5: no filter bar; dates/columns driven by dock Period/Columns menus.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "assets/css/history-table.css";
import moment from "moment";
import { api } from "utils/api";
import { getSessionData } from "utils/common";
import {
  formatUiDateTime,
  parseApiValueAsUtcDate,
  UI_DATEPICKER_FORMAT,
} from "utils/dateFormats";
import useMobileViewport from "hooks/useMobileViewport";
import useAppLanguage from "hooks/useAppLanguage";
import { tSide } from "utils/appLanguage";
import { getMarginSummary } from "Store/Reducer/margin.slice";
import {
  getLastChartSelection,
  subscribeLastChartSelection,
} from "utils/lastChartSelection";

const TABS = [
  { id: "closed", label: "Closed Positions" },
  { id: "deals", label: "Deals" },
  { id: "orders", label: "Orders" },
];

/** Mobile MT5 History tabs (screenshot labels). */
const MT5_TABS = [
  { id: "closed", label: "Positions" },
  { id: "orders", label: "Orders" },
  { id: "deals", label: "Deals" },
];

/** MT5 History period menu (clock icon). */
const HISTORY_PERIODS = [
  { id: "today", labelKey: "today", label: "Today", days: 0 },
  { id: "week", labelKey: "lastWeek", label: "Last week", days: 7 },
  { id: "month", labelKey: "lastMonth", label: "Last Month", months: 1 },
  { id: "months3", labelKey: "last3Months", label: "Last 3 Months", months: 3 },
  { id: "months6", labelKey: "last6Months", label: "Last 6 Months", months: 6 },
  { id: "year", labelKey: "lastYear", label: "Last year", years: 1 },
  { id: "all", labelKey: "allHistory", label: "All History", all: true },
  { id: "custom", labelKey: "customPeriod", label: "Custom Period", days: null },
];

/** MT5 History Sort By sheet. */
const HISTORY_SORTS = [
  { id: "default", labelKey: "byDefault", label: "By Default" },
  { id: "openTime", labelKey: "openTimeSort", label: "Open Time" },
  { id: "ticket", labelKey: "ticket", label: "Ticket" },
  { id: "type", labelKey: "type", label: "Type" },
  { id: "volume", labelKey: "volume", label: "Volume" },
  { id: "symbol", labelKey: "symbol", label: "Symbol" },
  { id: "closeTime", labelKey: "closeTime", label: "Close Time" },
  { id: "profit", labelKey: "profit", label: "Profit" },
];

const HISTORY_PERIOD_STORAGE = "fx_hist_period_v1";
const HISTORY_OPEN_PERIOD_EVENT = "fxb-history-open-period";

/** Calendar YYYY-MM-DD for History API filters (local day — no TZ day-shift). */
function toHistoryFilterYmd(d) {
  if (typeof d === "string" && /^\d{4}-\d{2}-\d{2}/.test(d.trim())) {
    return d.trim().slice(0, 10);
  }
  const m = moment(d);
  return m.isValid() ? m.format("YYYY-MM-DD") : moment().format("YYYY-MM-DD");
}

function readStoredHistoryPeriod() {
  try {
    return localStorage.getItem(HISTORY_PERIOD_STORAGE) || "months3";
  } catch {
    return "months3";
  }
}

function periodRangeForId(periodId) {
  const end = moment().endOf("day").toDate();
  if (periodId === "custom") return null;
  if (periodId === "today") {
    return { start: moment().startOf("day").toDate(), end };
  }
  if (periodId === "all") {
    return { start: moment("2000-01-01").startOf("day").toDate(), end };
  }
  const p = HISTORY_PERIODS.find((x) => x.id === periodId) || HISTORY_PERIODS[2];
  if (p.years != null) {
    return {
      start: moment().subtract(p.years, "years").startOf("day").toDate(),
      end,
    };
  }
  if (p.months != null) {
    return {
      start: moment().subtract(p.months, "months").startOf("day").toDate(),
      end,
    };
  }
  return {
    start: moment().subtract(p.days || 0, "days").startOf("day").toDate(),
    end,
  };
}

function formatPeriodMenuRange(periodId) {
  const range = periodRangeForId(periodId);
  if (!range || periodId === "all") return "";
  if (periodId === "today") return moment(range.end).format("DD.MM.YYYY");
  return `${moment(range.start).format("DD.MM.YYYY")} — ${moment(range.end).format("DD.MM.YYYY")}`;
}

function formatCustomStripDate(d) {
  if (!d) return "—";
  return moment(d).format("DD/MM/YYYY");
}

function histRowTimeMs(row, preferClose = false) {
  const candidates = preferClose
    ? [
        row.closeTime,
        row.CloseTime,
        row.closeTimeText,
        row.CloseTimeText,
        row.timeDone,
        row.TimeDone,
        row.createdOn,
        row.CreatedOn,
        row.openTime,
        row.OpenTime,
      ]
    : [
        row.openTime,
        row.OpenTime,
        row.openTimeText,
        row.OpenTimeText,
        row.timeSetup,
        row.TimeSetup,
        row.createdOn,
        row.CreatedOn,
        row.closeTime,
        row.CloseTime,
      ];
  for (const v of candidates) {
    const d = parseApiValueAsUtcDate(v);
    if (d && !Number.isNaN(d.getTime())) return d.getTime();
  }
  return 0;
}

function sortHistoryRows(list, sortId, tabId) {
  if (!list?.length || !sortId || sortId === "default") return list || [];
  const copy = [...list];
  const cmpStr = (a, b) => String(a || "").localeCompare(String(b || ""), undefined, { sensitivity: "base" });
  const cmpNum = (a, b) => (Number(a) || 0) - (Number(b) || 0);
  copy.sort((a, b) => {
    switch (sortId) {
      case "ticket":
        return cmpNum(
          b.id ?? b.Id ?? b.positionId ?? b.PositionId,
          a.id ?? a.Id ?? a.positionId ?? a.PositionId
        );
      case "type":
        return cmpStr(
          a.dealTypeName || a.orderTypeName || a.positionTypeName || a.type || a.Type,
          b.dealTypeName || b.orderTypeName || b.positionTypeName || b.type || b.Type
        );
      case "volume":
        return cmpNum(
          b.volumeLots ?? b.VolumeLots ?? b.lot ?? b.Lot,
          a.volumeLots ?? a.VolumeLots ?? a.lot ?? a.Lot
        );
      case "symbol":
        return cmpStr(
          a.symbol || a.Symbol || a.displayName || a.DisplayName,
          b.symbol || b.Symbol || b.displayName || b.DisplayName
        );
      case "closeTime":
        return histRowTimeMs(b, true) - histRowTimeMs(a, true);
      case "profit": {
        const pa =
          tabId === "closed"
            ? Number(a.realizedPnl ?? a.RealizedPnl) || 0
            : Number(a.profit ?? a.Profit) || 0;
        const pb =
          tabId === "closed"
            ? Number(b.realizedPnl ?? b.RealizedPnl) || 0
            : Number(b.profit ?? b.Profit) || 0;
        return pb - pa;
      }
      case "openTime":
      default:
        return histRowTimeMs(b, false) - histRowTimeMs(a, false);
    }
  });
  return copy;
}

const VALID_TABS = new Set(TABS.map((t) => t.id));

function endpointForTab(tab) {
  if (tab === "orders") return "orders/get-history";
  if (tab === "closed") return "positions/get-closed";
  return "deals/get";
}

function fmt(v) {
  if (v == null || v === "") return "—";
  return String(v);
}

const HISTORY_EXPIRY_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** Strip trailing expiry (`28Apr2026`) so History rows show symbol only. */
function stripExpiryFromSymbol(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const stripped = raw.replace(/\s*\d{1,2}[A-Za-z]{3}\d{4}\s*$/i, "").trim();
  return stripped || raw;
}

function historySymbolLabel(row) {
  const raw = String(
    row?.symbol ||
      row?.Symbol ||
      row?.script ||
      row?.Script ||
      row?.displayName ||
      row?.DisplayName ||
      ""
  ).trim();
  return stripExpiryFromSymbol(raw) || "—";
}

/** Cash / ledger deal — sheet shows "Balance"; Chart/New Order use last selected symbol. */
function isHistoryBalanceLike(row) {
  if (!row || typeof row !== "object") return false;
  const names = [
    row.symbol,
    row.Symbol,
    row.script,
    row.Script,
    row.displayName,
    row.DisplayName,
  ]
    .map((v) => String(v ?? "").trim())
    .filter(Boolean);
  if (names.some((n) => /^balance$/i.test(n))) return true;
  const hasInstrument = names.length > 0;
  if (hasInstrument) return false;
  const tfBal = String(row.tradeFrom || row.TradeFrom || "").toLowerCase();
  return (
    tfBal.includes("balance") ||
    tfBal.includes("pay-out") ||
    tfBal.includes("payout") ||
    tfBal.includes("pay-in") ||
    tfBal.includes("payin") ||
    tfBal.includes("withdraw") ||
    tfBal.includes("deposit")
  );
}

function expirySuffixFromText(value) {
  const m = String(value || "")
    .trim()
    .match(/(\d{1,2}[A-Za-z]{3}\d{4})\s*$/i);
  return m ? m[1] : "";
}

/** Same date-key normalize Quotes uses (`DD-MM-YYYY`). */
function normalizeHistoryDateStr(raw) {
  if (!raw) return "";
  const s = String(raw).trim();
  if (/^\d{2}-\d{2}-\d{4}$/.test(s)) return s;
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const [yyyy, mm, dd] = s.slice(0, 10).split("-");
    return `${dd}-${mm}-${yyyy}`;
  }
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s.replace(/\//g, "-");
  if (/^\d{8}$/.test(s)) {
    return `${s.slice(6, 8)}-${s.slice(4, 6)}-${s.slice(0, 4)}`;
  }
  if (/^\d{4}\/\d{2}\/\d{2}$/.test(s)) {
    const [yyyy, mm, dd] = s.split("/");
    return `${dd}-${mm}-${yyyy}`;
  }
  return "";
}

function buildHistoryExpirySuffix(ddmmyyyy) {
  const parts = String(ddmmyyyy || "").split("-");
  if (parts.length !== 3) return "";
  const [dd, mm, yyyy] = parts;
  const mon = HISTORY_EXPIRY_MONTHS[Number(mm) - 1];
  if (!mon || !dd || !yyyy) return "";
  return `${dd}${mon}${yyyy}`;
}

function pickHistoryExpiryFromList(list) {
  if (!Array.isArray(list) || list.length === 0) return "";
  for (const item of list) {
    const candidates =
      typeof item === "string"
        ? [item]
        : [
            item?.label,
            item?.name,
            item?.expiry,
            item?.value,
            item?.date,
            item?.ExpiryDate,
            item?.expiryDate,
            item?.ExDate,
            item?.exDate,
          ].filter(Boolean);
    for (const c of candidates) {
      const suffix = expirySuffixFromText(c);
      if (suffix) return suffix;
      const ds = normalizeHistoryDateStr(String(c));
      if (ds) return buildHistoryExpirySuffix(ds);
    }
  }
  return "";
}

function extractHistoryExpiryApiList(res) {
  const body = res?.data;
  if (body == null) return [];
  if (Array.isArray(body)) return body;
  const arr = body.data ?? body.Data ?? body.records;
  if (Array.isArray(arr)) return arr;
  if (Array.isArray(body.data?.data)) return body.data.data;
  return [];
}

function formatHistoryExpiryRaw(expRaw) {
  if (expRaw == null || expRaw === "") return "";
  if (expRaw instanceof Date) {
    const m = moment(expRaw);
    return m.isValid() && m.year() > 2000 ? m.format("DDMMMYYYY") : "";
  }
  const s = String(expRaw).trim();
  if (!s || s === "0") return "";
  const suffix = expirySuffixFromText(s);
  if (suffix) return suffix;
  if (/^0001/.test(s) || /^2000-01-01/.test(s)) return "";
  const nd = normalizeHistoryDateStr(s);
  if (nd) return buildHistoryExpirySuffix(nd);
  const parsed = moment(
    s,
    ["DD-MM-YYYY", "YYYY-MM-DD", "DD/MM/YYYY", "YYYYMMDD", "YYYY/MM/DD", moment.ISO_8601],
    true
  );
  if (parsed.isValid() && parsed.year() > 2000) return parsed.format("DDMMMYYYY");
  const loose = moment(s);
  return loose.isValid() && loose.year() > 2000 ? loose.format("DDMMMYYYY") : "";
}

function historyExpiryFromRowFields(row) {
  if (!row || typeof row !== "object") return "";
  const fromName = [
    row.displayName,
    row.DisplayName,
    row.script,
    row.Script,
    row.symbol,
    row.Symbol,
    row.name,
    row.Name,
  ];
  for (const v of fromName) {
    const suffix = expirySuffixFromText(v);
    if (suffix) return suffix;
  }
  const expRaw =
    row.exDate ??
    row.ExDate ??
    row.expiryDate ??
    row.ExpiryDate ??
    row.expiryDateTime ??
    row.ExpiryDateTime ??
    row.expiry ??
    row.Expiry;
  const formatted = formatHistoryExpiryRaw(expRaw);
  if (formatted) return formatted;
  for (const [key, value] of Object.entries(row)) {
    if (!/display|script|symbol|expiry|exdate/i.test(String(key))) continue;
    const suffix = expirySuffixFromText(value);
    if (suffix) return suffix;
    const fromVal = formatHistoryExpiryRaw(value);
    if (fromVal) return fromVal;
  }
  return "";
}

function historyWatchlistRows(watchList) {
  if (!watchList) return [];
  if (watchList instanceof Map) return Array.from(watchList.values());
  if (Array.isArray(watchList)) return watchList;
  if (typeof watchList.values === "function") {
    try {
      return Array.from(watchList.values());
    } catch {
      return [];
    }
  }
  if (typeof watchList === "object") return Object.values(watchList);
  return [];
}

function historyWantNames(row) {
  return new Set(
    [row?.symbol, row?.Symbol, row?.script, row?.Script, row?.displayName, row?.DisplayName]
      .map((v) => stripExpiryFromSymbol(v).toUpperCase())
      .filter(Boolean)
  );
}

function historyWatchNameHits(wantNames, watchName) {
  const full = String(watchName || "").trim().toUpperCase();
  if (!full) return false;
  const stripped = stripExpiryFromSymbol(full).toUpperCase();
  if (wantNames.has(stripped) || wantNames.has(full)) return true;
  for (const want of wantNames) {
    if (full === want || full.startsWith(`${want} `) || stripped.startsWith(`${want} `)) return true;
  }
  return false;
}

function historyExpiryFromWatchlist(row, watchList, scriptIndexList) {
  const list = [
    ...historyWatchlistRows(watchList),
    ...historyWatchlistRows(scriptIndexList),
  ];
  if (!row || !list.length) return "";
  const wantSeg = Number(row.segmentId ?? row.SegmentId);
  const wantNames = historyWantNames(row);
  if (!wantNames.size) return "";
  let fallback = "";
  for (const w of list) {
    if (!w || typeof w !== "object") continue;
    const wNames = [w.symbol, w.script, w.displayName, w.Symbol, w.Script, w.DisplayName, w.name];
    if (!wNames.some((n) => historyWatchNameHits(wantNames, n))) continue;
    const exp = historyExpiryFromRowFields(w);
    if (!exp) continue;
    const wSeg = Number(w.segmentId ?? w.SegmentId);
    if (Number.isFinite(wantSeg) && wantSeg > 0 && Number.isFinite(wSeg) && wSeg === wantSeg) {
      return exp;
    }
    if (!fallback) fallback = exp;
  }
  return fallback;
}

/** Expiry for History tap popup only — not attached to the list symbol. */
function historyExpiryLabel(row, watchList, scriptIndexList) {
  return (
    historyExpiryFromRowFields(row) ||
    historyExpiryFromWatchlist(row, watchList, scriptIndexList)
  );
}

function fmtTime(...vals) {
  for (const v of vals) {
    const t = formatUiDateTime(v, "");
    if (t) return t;
  }
  return "—";
}

/** MT5 History timestamp: 2026.10.14 09:21:44 */
function fmtMt5Time(...vals) {
  for (const v of vals) {
    const d = parseApiValueAsUtcDate(v);
    if (!d) continue;
    try {
      const parts = new Intl.DateTimeFormat("en-GB", {
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        hour12: false,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }).formatToParts(d);
      const get = (type) => parts.find((p) => p.type === type)?.value || "";
      let hh = get("hour");
      if (hh === "24") hh = "00";
      return `${get("year")}.${get("month")}.${get("day")} ${hh}:${get("minute")}:${get("second")}`;
    } catch {
      return moment(d).format("YYYY.MM.DD HH:mm:ss");
    }
  }
  return "—";
}

/** Closed-position header: keep both open/close times without overflowing. */
function fmtMt5TimeRange(openT, closeT) {
  const open = openT && openT !== "—" ? openT : "";
  const close = closeT && closeT !== "—" ? closeT : "";
  if (!open && !close) return "";
  if (!open) return close;
  if (!close) return open;
  const openDay = open.slice(0, 10);
  const closeDay = close.slice(0, 10);
  if (openDay === closeDay && close.length > 11) {
    return `${open} — ${close.slice(11)}`;
  }
  return `${open} — ${close}`;
}

function fmtMt5Num(n, digits = 2) {
  const x = n == null || n === "" ? 0 : Number(n);
  const v = Number.isFinite(x) ? x : 0;
  return v.toLocaleString("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

/** First finite decimal from camelCase / PascalCase API fields (incl. 0). */
function pickDecimal(...vals) {
  for (const v of vals) {
    if (v == null || v === "") continue;
    const n = typeof v === "number" ? v : Number(String(v).replace(/,/g, "").trim());
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/** MT5 deal popup: empty / zero Charges and Swap show as "-" not 0.00. */
function fmtMt5SheetDash(n) {
  const v = pickDecimal(n);
  if (v == null || v === 0) return "-";
  return fmtMt5Num(v);
}

/** Keep instrument digits when the API sent them; empty → 0.00 (MT5). */
function fmtMt5Rate(v) {
  const n = pickDecimal(v);
  if (n == null) return "0.00";
  const raw = String(v).trim().replace(/,/g, "");
  if (/^-?\d+\.\d+$/.test(raw)) return raw;
  return n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 8,
  });
}

/** Cash Ids from API use offset 1_000_000_000_000 (HistoryData.BalanceIdOffset). */
const HISTORY_BALANCE_ID_OFFSET = 1_000_000_000_000;

/** Cash / ledger deals with no symbol — MT5 History lists these as Balance. */
function isBalanceDeal(row) {
  if (!row) return false;
  const rawId = Number(
    row.positionId ?? row.PositionId ?? row.id ?? row.Id ?? row.dealId ?? row.DealId
  );
  if (Number.isFinite(rawId) && rawId >= HISTORY_BALANCE_ID_OFFSET) return true;

  const tf = String(row.tradeFrom || row.TradeFrom || "").toLowerCase();
  if (
    !(
      tf.includes("balance") ||
      tf.includes("pay-out") ||
      tf.includes("payout") ||
      tf.includes("pay-in") ||
      tf.includes("payin") ||
      tf.includes("withdraw") ||
      tf.includes("deposit")
    )
  ) {
    return false;
  }
  const symbol = String(
    row.symbol ||
      row.Symbol ||
      row.displayName ||
      row.DisplayName ||
      row.script ||
      row.Script ||
      ""
  ).trim();
  return !symbol;
}

/** Map a Deals-tab Balance row into Closed Positions shape (Positions list). */
function balanceDealAsClosedRow(row) {
  const when =
    row.createdOn ??
    row.CreatedOn ??
    row.dealTime ??
    row.DealTime ??
    row.closeTime ??
    row.CloseTime;
  const text = row.dealTime ?? row.DealTime ?? row.closeTimeText ?? row.CloseTimeText ?? "";
  const amount = balanceDealAmount(row);
  const id = row.id ?? row.Id ?? row.positionId ?? row.PositionId;
  return {
    ...row,
    positionId: id,
    PositionId: id,
    realizedPnl: amount,
    RealizedPnl: amount,
    profit: amount,
    Profit: amount,
    openTime: when,
    OpenTime: when,
    closeTime: when,
    CloseTime: when,
    openTimeText: text,
    OpenTimeText: text,
    closeTimeText: text,
    CloseTimeText: text,
    tradeFrom: row.tradeFrom || row.TradeFrom || "pay-in",
    TradeFrom: row.tradeFrom || row.TradeFrom || "pay-in",
    symbol: "",
    Symbol: "",
    displayName: "",
    DisplayName: "",
    script: "",
    Script: "",
    type: "",
    Type: "",
  };
}

function balanceDealAmount(row) {
  return (
    pickDecimal(
      row.profit,
      row.Profit,
      row.realizedPnl,
      row.RealizedPnl,
      row.price,
      row.Price
    ) ?? 0
  );
}

/** GetDeals returns Profit only on Out; In is null — list still shows 0.00. */
function dealProfitNumber(row) {
  return pickDecimal(row?.profit, row?.Profit) ?? 0;
}

/** MT5 deal popup hides P/L on In; list still shows 0.00. */
function isDealEntryIn(entry) {
  const s = String(entry || "").trim().toLowerCase();
  if (!s) return false;
  if (s === "out" || s.includes("out")) return false;
  return s === "in" || /\bin\b/.test(s);
}

/** Same commission the web History column uses (`accessor: "commission"`). */
function getRowCommission(row) {
  if (!row || typeof row !== "object") return null;
  const direct = pickDecimal(
    row.commission,
    row.Commission,
    row.commision,
    row.Commision,
    row.brokerage,
    row.Brokerage,
    row.charges,
    row.Charges
  );
  if (direct != null) return direct;
  for (const key of Object.keys(row)) {
    const lk = String(key).toLowerCase();
    if (lk === "commission" || lk === "commision" || lk === "brokerage" || lk === "charges") {
      const n = pickDecimal(row[key]);
      if (n != null) return n;
    }
  }
  return null;
}

/** Display like web column — never hide a present numeric commission. */
function formatRowCommission(row) {
  return fmtMt5Num(getRowCommission(row) ?? 0);
}

/** Ensure camelCase commission on every history row (web + mobile share this). */
function normalizeHistoryRow(row) {
  if (!row || typeof row !== "object") return row;
  const commission = getRowCommission(row);
  const script = row.script ?? row.Script;
  const expiryDate = row.expiryDate ?? row.ExpiryDate ?? row.exDate ?? row.ExDate;
  const next = { ...row };
  if (commission != null) next.commission = commission;
  if (script != null && script !== "" && next.script == null) next.script = script;
  if (expiryDate != null && expiryDate !== "" && next.expiryDate == null) next.expiryDate = expiryDate;
  return next;
}

/** MT5 deals: positive = blue, negative = red (no + prefix). Empty → 0.00. */
function mt5ProfitTone(n) {
  const v = n == null || !Number.isFinite(Number(n)) ? 0 : Number(n);
  const text = fmtMt5Num(v);
  if (v > 0) return { color: "#1e5eff", tone: "up", text };
  if (v < 0) return { color: "#c62828", tone: "down", text };
  return { color: undefined, tone: "flat", text };
}

function entryTone(entry) {
  const s = String(entry || "").toLowerCase();
  if (s === "out" || s.includes("out")) return "sell";
  if (s === "in" || s.includes("in")) return "buy";
  return "flat";
}

function isHistoryOrderPending(row) {
  if (!row) return false;
  const st = Number(row.state ?? row.State ?? 0);
  const status = String(row.status || row.Status || row.stateName || row.StateName || "").toLowerCase();
  if (status === "filled" || status === "cancelled" || status === "expired" || status === "rejected") {
    return false;
  }
  if (st === 2 || st === 3 || st === 4 || st === 5) return false;
  return st === 0 || st === 1 || status === "pending" || status === "placed" || status.includes("partial");
}

function extractHistoryRecords(res) {
  const status = res?.status ?? res?.Status;
  if (status != null && Number(status) !== 200) {
    throw new Error(res?.message || res?.Message || "History API failed");
  }
  const inner = res?.data ?? res?.Data ?? res;
  const records =
    inner?.records ||
    inner?.Records ||
    inner?.data?.records ||
    (Array.isArray(inner) ? inner : []);
  const total = inner?.totalRecords ?? inner?.TotalRecords ?? inner?.data?.totalRecords ?? records.length;
  return { records: Array.isArray(records) ? records : [], total: Number(total) || 0 };
}

async function waitForSessionUid(maxMs = 2500) {
  const started = Date.now();
  while (Date.now() - started < maxMs) {
    const sess = getSessionData() || {};
    const uid = sess.userId || sess.id || sess.UserId;
    if (uid) return { sess, uid };
    await new Promise((r) => setTimeout(r, 120));
  }
  const sess = getSessionData() || {};
  const uid = sess.userId || sess.id || sess.UserId;
  return uid ? { sess, uid } : null;
}

async function postHistoryWithRetry(endpoint, payload, attempts = 2) {
  let lastErr;
  for (let i = 0; i < attempts; i += 1) {
    try {
      const { data: res } = await api.post(endpoint, payload);
      return extractHistoryRecords(res);
    } catch (e) {
      lastErr = e;
      const status = e?.response?.status;
      // 400/403/404 will not succeed on retry. 401 is already retried in api.js.
      if (status && status !== 408 && status !== 502 && status !== 503 && status !== 504) {
        break;
      }
      if (i < attempts - 1) {
        await new Promise((r) => setTimeout(r, 280 * (i + 1)));
      }
    }
  }
  throw lastErr;
}

function colOn(map, id) {
  // No map (full-page History): show all columns in that table.
  if (!map || typeof map !== "object") return true;
  // Explicit false = hidden; missing key = off for dock (map is always fully keyed per tab).
  return map[id] === true;
}

function histHead(label, className, tip) {
  return (
    <th className={className} data-tip={tip || label}>
      {label}
    </th>
  );
}

function sideTone(label) {
  const s = String(label || "").toLowerCase();
  if (s.includes("buy") || s === "buy") return "buy";
  if (s.includes("sell") || s === "sell") return "sell";
  return "flat";
}

function profitTone(n) {
  if (n == null || !Number.isFinite(n)) {
    return { color: undefined, tone: "flat", text: "—" };
  }
  const formatted = Number(n).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (n === 0) return { color: undefined, tone: "flat", text: formatted };
  const color = n > 0 ? "#059669" : "#e11d48";
  const prefix = n > 0 ? "+" : "";
  return { color, tone: n > 0 ? "up" : "down", text: `${prefix}${formatted}` };
}

/** Build MT5 bottom-sheet model from our History row (no invented fields). */
function buildHistMt5Sheet(kind, row, watchList, scriptIndexList) {
  if (!row) return null;
  if (kind === "deal") {
    const rawId = row.id ?? row.Id;
    const id = isBalanceDeal(row) ? "" : rawId;
    const dealType = Number(row.dealType ?? row.DealType);
    const typeName = String(row.dealTypeName || row.DealTypeName || "").toLowerCase();
    const isBuy = dealType === 1 || typeName === "buy";
    const isSell = dealType === 2 || typeName === "sell";
    const side = isBuy
      ? "buy"
      : isSell
        ? "sell"
        : String(row.dealTypeName || row.DealTypeName || "").toLowerCase() || "—";
    const entry = String(row.entryName || row.EntryName || "").trim().toLowerCase() || "";
    const profitNum = dealProfitNumber(row);
    const pnl = mt5ProfitTone(profitNum);
    const displayName = row.displayName || row.DisplayName || "";
    const symbol = historySymbolLabel(row);
    const expiry = historyExpiryLabel(row, watchList, scriptIndexList);
    const chartSym = String(
      row.script || row.Script || row.symbol || row.Symbol || displayName || ""
    ).trim();
    const time = fmtMt5Time(row.createdOn, row.CreatedOn, row.dealTime, row.DealTime);
    const lots = row.volumeLots ?? row.VolumeLots ?? row.lot;
    const price = row.price ?? row.Price;
    const lotsTxt = fmtMt5Num(pickDecimal(lots) ?? 0);
    const priceTxt = fmtMt5Rate(price);
    const isBalanceLike = isBalanceDeal(row);
    const balAmt = mt5ProfitTone(balanceDealAmount(row));
    const orderId = row.orderId ?? row.OrderId;
    const charges = pickDecimal(row.commission, row.Commission, row.charges, row.Charges);
    const swap = pickDecimal(row.swap, row.Swap, row.storage, row.Storage);
    const comment = String(row.comment || row.Comment || "").trim();
    return {
      kind,
      row,
      id,
      symbol: isBalanceLike ? "Balance" : symbol,
      expiry: "",
      chartSymbol: isBalanceLike ? "" : chartSym,
      useLastSelectedSymbol: isBalanceLike,
      time,
      side: isBalanceLike ? "" : side,
      sTone: isBalanceLike ? "flat" : sideTone(side),
      entry: isBalanceLike ? "" : entry,
      eTone: isBalanceLike ? "flat" : entryTone(entry),
      subtitle: isBalanceLike ? "" : `${lotsTxt} at ${priceTxt}`,
      rightPrimary: isBalanceLike
        ? {
            text: balAmt.text,
            color: balAmt.color || (balAmt.tone === "down" ? "#c62828" : "#1e5eff"),
          }
        : isDealEntryIn(entry)
          ? null
          : { text: pnl.text, color: pnl.color },
      comment,
      fields: isBalanceLike
        ? []
        : [
            { key: "order", value: orderId == null || orderId === "" ? "-" : String(orderId) },
            { key: "charges", value: fmtMt5SheetDash(charges) },
            { key: "swap", value: fmtMt5SheetDash(swap) },
          ],
    };
  }
  if (kind === "order") {
    const id = row.id ?? row.Id;
    const side = mt5OrderSideText(row);
    const displayName = row.displayName || row.DisplayName || "";
    const symbol = historySymbolLabel(row);
    const expiry = historyExpiryLabel(row, watchList, scriptIndexList);
    const chartSym = String(
      row.script || row.Script || row.symbol || row.Symbol || displayName || ""
    ).trim();
    const setupTime = fmtMt5Time(
      row.timeSetup,
      row.TimeSetup,
      row.timeSetupText,
      row.TimeSetupText,
      row.addTime
    );
    const doneTime = fmtMt5Time(
      row.timeDone,
      row.TimeDone,
      row.timeDoneText,
      row.TimeDoneText,
      row.tradeDateTime
    );
    const time = doneTime !== "—" ? doneTime : setupTime;
    const qty = pickDecimal(row.qty, row.Qty);
    const rate = pickDecimal(row.rate, row.Rate);
    const status = orderStatusLabel(row);
    return {
      kind,
      row,
      id,
      symbol,
      expiry,
      chartSymbol: chartSym,
      time,
      side,
      sTone: sideTone(side),
      entry: "",
      eTone: "flat",
      subtitle: mt5OrderVolumeLine(row),
      rightPrimary: { text: status, color: "#888" },
      fields: [
        { key: "order", value: fmt(id) },
        { key: "qty", value: fmtMt5Num(qty ?? 0) },
        { key: "price", value: fmtMt5Rate(rate) },
      ],
    };
  }
  // closed positions
  const isClosedBalance = isBalanceDeal(row);
  const id = isClosedBalance ? "" : (row.id ?? row.Id ?? row.positionId ?? row.PositionId);
  const typeName = String(row.positionTypeName || row.type || row.Type || "").toLowerCase();
  const isBuy = typeName === "buy" || Number(row.positionType) === 1;
  const isSell = typeName === "sell" || Number(row.positionType) === 2;
  const side = isBuy ? "buy" : isSell ? "sell" : typeName || "—";
  const displayName = row.displayName || row.DisplayName || "";
  const symbol = historySymbolLabel(row);
  const expiry = historyExpiryLabel(row, watchList, scriptIndexList);
  const chartSym = String(
    row.script || row.Script || row.symbol || row.Symbol || displayName || ""
  ).trim();
  const lots = row.volumeLots ?? row.VolumeLots ?? row.lot ?? row.Lot;
  const lotsTxt = fmtMt5Num(pickDecimal(lots) ?? 0);
  const qty = pickDecimal(row.volumeQty, row.VolumeQty, row.qty, row.Qty);
  const openPx = row.priceOpen ?? row.PriceOpen;
  const closePx = row.priceClose ?? row.PriceClose;
  const pnlNum = pickDecimal(row.realizedPnl, row.RealizedPnl) ?? 0;
  const pnl = mt5ProfitTone(pnlNum);
  const openT = fmtMt5Time(
    row.openTime,
    row.OpenTime,
    row.openTimeText,
    row.OpenTimeText,
    row.timeCreate
  );
  const closeT = fmtMt5Time(
    row.closeTime,
    row.CloseTime,
    row.closeTimeText,
    row.CloseTimeText,
    row.timeClose
  );
  const time = closeT !== "—" ? closeT : openT;
  if (isClosedBalance) {
    const balAmt = mt5ProfitTone(balanceDealAmount(row));
    return {
      kind,
      row,
      id: "",
      symbol: "Balance",
      expiry: "",
      chartSymbol: "",
      time,
      side: "",
      sTone: "flat",
      entry: "",
      eTone: "flat",
      subtitle: "",
      rightPrimary: {
        text: balAmt.text,
        color: balAmt.color || (balAmt.tone === "down" ? "#c62828" : "#1e5eff"),
      },
      metaLine: "",
      fields: [],
    };
  }
  return {
    kind,
    row,
    id,
    symbol,
    expiry,
    chartSymbol: chartSym,
    time,
    side: `${side} ${lotsTxt}`,
    sTone: sideTone(side),
    entry: "",
    eTone: "flat",
    subtitle: `${fmtMt5Rate(openPx)} → ${fmtMt5Rate(closePx)}`,
    rightPrimary: { text: pnl.text, color: pnl.color },
    // Open/close times only under #id (metaLine) — not repeated in field grid.
    metaLine: fmtMt5TimeRange(openT, closeT),
    fields: [
      { key: "positionTicket", value: fmt(row.positionId ?? row.PositionId ?? id) },
      { key: "qty", value: fmtMt5Num(qty ?? 0) },
      { key: "commission", value: formatRowCommission(row) },
    ],
  };
}

function HistMt5DetailSheet({
  model,
  onClose,
  onChart,
  onNewOrder,
  actionsEnabled = true,
}) {
  const { t, languageId } = useAppLanguage();
  if (!model) return null;
  const canAct = actionsEnabled !== false;
  const sideClass = model.sTone ? ` hist-mt5-sheet__side--${model.sTone}` : "";
  const entryClass = model.eTone ? ` hist-mt5-sheet__entry--${model.eTone}` : "";
  const fieldPairs = [];
  const fields = model.fields || [];
  for (let i = 0; i < fields.length; i += 2) {
    fieldPairs.push([fields[i], fields[i + 1] || null]);
  }
  const headerTime = model.kind === "closed" && model.metaLine ? model.metaLine : model.time;
  const isClosedSpan = model.kind === "closed" && !!model.metaLine;
  const ticketLabel = model.id !== "" && model.id != null ? `#${fmt(model.id)}` : "";
  return (
    <div className="hist-mt5-sheet-root" role="presentation">
      <button
        type="button"
        className="hist-mt5-sheet-backdrop"
        aria-label={t("close")}
        onClick={onClose}
      />
      <div className={`hist-mt5-sheet${model.kind ? ` hist-mt5-sheet--${model.kind}` : ""}`} role="dialog" aria-label={t("history")}>
        {/* Positions: #id then date. Deals/Orders: time then #id. */}
        <div
          className={`hist-mt5-sheet__id-row${
            isClosedSpan ? " hist-mt5-sheet__id-row--span" : ""
          }`}
        >
          {isClosedSpan ? (
            <>
              <span className="hist-mt5-sheet__id">{ticketLabel}</span>
              <span className="hist-mt5-sheet__time">{headerTime || ""}</span>
            </>
          ) : (
            <>
              <span className="hist-mt5-sheet__time">{headerTime || ""}</span>
              <span className="hist-mt5-sheet__id">{ticketLabel}</span>
            </>
          )}
        </div>
        <div className="hist-mt5-sheet__main">
          <div className="hist-mt5-sheet__left">
            <div className="hist-mt5-sheet__line1">
              <span className="hist-mt5-sheet__symbol">{model.symbol}</span>
              {model.side && model.side !== "—" ? (
                <span className={`hist-mt5-sheet__side${sideClass}`}>
                  {"\u00A0"}
                  {tSide(model.side, languageId)}
                </span>
              ) : null}
              {model.entry ? (
                <span className={`hist-mt5-sheet__entry${entryClass}`}>{`, ${model.entry}`}</span>
              ) : null}
            </div>
            {model.expiry ? (
              <div className="hist-mt5-sheet__expiry">{model.expiry}</div>
            ) : null}
            {model.subtitle ? (
              <div className="hist-mt5-sheet__line2">{model.subtitle}</div>
            ) : null}
          </div>
          {model.rightPrimary ? (
            <div
              className="hist-mt5-sheet__right"
              style={model.rightPrimary.color ? { color: model.rightPrimary.color } : undefined}
            >
              {model.rightPrimary.text}
            </div>
          ) : null}
        </div>
        {fieldPairs.length ? (
          <div className="hist-mt5-sheet__fields">
            {fieldPairs.map((pair, idx) => (
              <div key={`fp-${idx}`} className="hist-mt5-sheet__field-row">
                {pair.map((f, j) =>
                  f ? (
                    <div key={f.key || f.label} className="hist-mt5-sheet__field">
                      <span className="hist-mt5-sheet__field-label">
                        {f.label || t(f.key)}:
                      </span>
                      <span className="hist-mt5-sheet__field-value">{f.value}</span>
                    </div>
                  ) : (
                    <div key={`empty-${j}`} className="hist-mt5-sheet__field" />
                  )
                )}
              </div>
            ))}
          </div>
        ) : null}
        {model.comment ? (
          <div className="hist-mt5-sheet__comment">{model.comment}</div>
        ) : null}
        <div className="hist-mt5-sheet__actions">
          <button
            type="button"
            className="hist-mt5-sheet__action"
            disabled={!canAct}
            onClick={() => {
              if (!canAct) return;
              onChart?.(model);
            }}
          >
            {t("chart")}
          </button>
          <button
            type="button"
            className="hist-mt5-sheet__action"
            disabled={!canAct}
            onClick={() => {
              if (!canAct) return;
              onNewOrder?.(model);
            }}
          >
            {t("newOrder")}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Mobile MT5 Deals row — tap selects; details open in bottom sheet.
 */
function Mt5DealRow({ row, selected, onToggle }) {
  const { languageId } = useAppLanguage();
  const id = row.id ?? row.Id;
  const dealType = Number(row.dealType ?? row.DealType);
  const typeName = String(row.dealTypeName || row.DealTypeName || "").toLowerCase();
  const isBuy = dealType === 1 || typeName === "buy";
  const isSell = dealType === 2 || typeName === "sell";
  const side = isBuy ? "buy" : isSell ? "sell" : String(row.dealTypeName || row.DealTypeName || "").toLowerCase() || "—";
  const entry = String(row.entryName || row.EntryName || "").trim().toLowerCase() || "";
  const eTone = entryTone(entry);
  const sTone = sideTone(side);
  const profitNum = dealProfitNumber(row);
  const pnl = mt5ProfitTone(profitNum);
  const symbol = historySymbolLabel(row);
  const time = fmtMt5Time(row.createdOn, row.CreatedOn, row.dealTime, row.DealTime);
  const lots = row.volumeLots ?? row.VolumeLots ?? row.lot;
  const price = row.price ?? row.Price;
  const lotsTxt = fmtMt5Num(pickDecimal(lots) ?? 0);
  const priceTxt = fmtMt5Rate(price);
  const isBalanceLike = isBalanceDeal(row);
  const balPnl = mt5ProfitTone(balanceDealAmount(row));

  if (isBalanceLike) {
    return (
      <button
        type="button"
        className={`hist-mt5-deal hist-mt5-deal--balance${selected ? " is-selected" : ""}`}
        onClick={onToggle}
      >
        <div className="hist-mt5-deal__main">
          <div className="hist-mt5-deal__left">
            <div className="hist-mt5-deal__line1">
              <span className="hist-mt5-deal__symbol">Balance</span>
            </div>
          </div>
          <div className="hist-mt5-deal__right">
            <span
              className={`hist-mt5-deal__pnl hist-mt5-deal__pnl--balance is-${balPnl.tone || "flat"}`}
              style={{ color: balPnl.color || (balPnl.tone === "down" ? "#c62828" : "#1e5eff") }}
            >
              {balPnl.text}
            </span>
            <span className="hist-mt5-deal__time">{time}</span>
          </div>
        </div>
      </button>
    );
  }

  return (
    <button
      type="button"
      className={`hist-mt5-deal hist-mt5-deal--${sTone}${selected ? " is-selected" : ""}`}
      data-deal-id={id}
      onClick={onToggle}
    >
      <div className="hist-mt5-deal__main">
        <div className="hist-mt5-deal__left">
          <div className="hist-mt5-deal__line1">
            <span className="hist-mt5-deal__symbol" title={symbol !== "—" ? symbol : undefined}>
              {symbol}
            </span>
            <span className={`hist-mt5-deal__side hist-mt5-deal__side--${sTone}`}>
              {"\u00A0"}
              {tSide(side, languageId)}
            </span>
            {entry ? (
              <span className={`hist-mt5-deal__entry hist-mt5-deal__entry--${eTone}`}>
                {`, ${entry}`}
              </span>
            ) : null}
          </div>
          <div className="hist-mt5-deal__line2">
            {lotsTxt} at {priceTxt}
          </div>
        </div>
        <div className="hist-mt5-deal__right">
          <span className="hist-mt5-deal__pnl" style={pnl.color ? { color: pnl.color } : undefined}>
            {pnl.text}
          </span>
          <span className="hist-mt5-deal__time">{time}</span>
        </div>
      </div>
    </button>
  );
}

function orderStatusLabel(row) {
  const key = orderStatusKey(row);
  if (key === "filled") return "Filled";
  if (key === "canceled") return "Canceled";
  if (key === "rejected") return "Rejected";
  if (key === "expired") return "Expired";
  const raw = String(
    row?.status || row?.Status || row?.stateName || row?.StateName || ""
  ).trim();
  return raw || "—";
}

/** MT5 order type: market vs buy/sell limit|stop. */
function mt5OrderExecKind(row) {
  const n = Number(row?.executionType ?? row?.ExecutionType);
  const name = String(row?.executionTypeName || row?.ExecutionTypeName || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ");
  if (n === 1 || name === "buylimit" || name.includes("buy limit")) return "buy limit";
  if (n === 2 || name === "buystop" || name.includes("buy stop")) return "buy stop";
  if (n === 3 || name === "selllimit" || name.includes("sell limit")) return "sell limit";
  if (n === 4 || name === "sellstop" || name.includes("sell stop")) return "sell stop";
  if (name.includes("limit")) return name.includes("sell") ? "sell limit" : "buy limit";
  if (name.includes("stop")) return name.includes("sell") ? "sell stop" : "buy stop";
  return "market";
}

/** Line 1 side: `buy` / `sell` or `buy limit` / `sell stop`. */
function mt5OrderSideText(row) {
  const kind = mt5OrderExecKind(row);
  if (kind !== "market") return kind;
  const typeName = String(row?.orderTypeName || row?.OrderTypeName || "").trim().toLowerCase();
  const typeNum = Number(row?.orderType ?? row?.OrderType);
  if (typeNum === 1 || typeName === "buy") return "buy";
  if (typeNum === 2 || typeName === "sell") return "sell";
  return typeName || "—";
}

/** Line 2: `1.00 / 1.00` on market; limit/stop add `at <rate>`. */
function mt5OrderVolumeLine(row) {
  const lotTxt = fmtMt5Num(pickDecimal(row?.lot, row?.Lot) ?? 0);
  const filled = `${lotTxt} / ${lotTxt}`;
  if (mt5OrderExecKind(row) === "market") return filled;
  const rate = pickDecimal(row?.rate, row?.Rate);
  if (rate == null) return filled;
  return `${filled} at ${fmtMt5Rate(rate)}`;
}

function orderStatusKey(row) {
  const st = Number(row?.state ?? row?.State ?? -1);
  const status = String(
    row?.status || row?.Status || row?.stateName || row?.StateName || ""
  ).toLowerCase();
  if (st === 5 || status === "rejected") return "rejected";
  if (st === 4 || status === "expired") return "expired";
  if (st === 3 || status === "cancelled" || status === "canceled") return "canceled";
  // Filled only — do not treat "partial fill" as filled (those stay pending / Trade)
  if (st === 2 || status === "filled") return "filled";
  return "other";
}

/**
 * Mobile MT5 closed Positions row — tap selects; details in bottom sheet.
 */
function Mt5ClosedRow({ row, selected, onToggle }) {
  const { languageId } = useAppLanguage();
  const id = row.id ?? row.Id ?? row.positionId ?? row.PositionId;
  const isBalanceLike = isBalanceDeal(row);
  const typeName = String(row.positionTypeName || row.type || row.Type || "").toLowerCase();
  const isBuy = typeName === "buy" || Number(row.positionType) === 1;
  const isSell = typeName === "sell" || Number(row.positionType) === 2;
  const side = isBuy ? "buy" : isSell ? "sell" : typeName || "—";
  const sTone = sideTone(side);
  const symbol = historySymbolLabel(row);
  const lots = row.volumeLots ?? row.VolumeLots ?? row.lot ?? row.Lot;
  const lotsTxt = fmtMt5Num(pickDecimal(lots) ?? 0);
  const openPx = row.priceOpen ?? row.PriceOpen;
  const closePx = row.priceClose ?? row.PriceClose;
  const pnlNum = pickDecimal(row.realizedPnl, row.RealizedPnl) ?? 0;
  const pnl = mt5ProfitTone(pnlNum);
  const openT = fmtMt5Time(
    row.openTime,
    row.OpenTime,
    row.openTimeText,
    row.OpenTimeText,
    row.timeCreate
  );
  const closeT = fmtMt5Time(
    row.closeTime,
    row.CloseTime,
    row.closeTimeText,
    row.CloseTimeText,
    row.timeClose
  );
  const time = closeT !== "—" ? closeT : openT;
  const balPnl = mt5ProfitTone(balanceDealAmount(row));

  if (isBalanceLike) {
    return (
      <button
        type="button"
        className={`hist-mt5-closed hist-mt5-closed--balance${selected ? " is-selected" : ""}`}
        data-position-id={id}
        onClick={onToggle}
      >
        <div className="hist-mt5-closed__main">
          <div className="hist-mt5-closed__left">
            <div className="hist-mt5-closed__line1">
              <span className="hist-mt5-closed__symbol">Balance</span>
            </div>
          </div>
          <div className="hist-mt5-closed__right">
            <span
              className={`hist-mt5-closed__pnl hist-mt5-closed__pnl--balance is-${balPnl.tone || "flat"}`}
              style={{ color: balPnl.color || (balPnl.tone === "down" ? "#c62828" : "#1e5eff") }}
            >
              {balPnl.text}
            </span>
            <span className="hist-mt5-closed__time">{time}</span>
          </div>
        </div>
      </button>
    );
  }

  return (
    <button
      type="button"
      className={`hist-mt5-closed hist-mt5-closed--${sTone}${selected ? " is-selected" : ""}`}
      data-position-id={id}
      onClick={onToggle}
    >
      <div className="hist-mt5-closed__main">
        <div className="hist-mt5-closed__left">
          <div className="hist-mt5-closed__line1">
            <span className="hist-mt5-closed__symbol" title={symbol !== "—" ? symbol : undefined}>
              {symbol}
            </span>
            <span className={`hist-mt5-closed__side hist-mt5-closed__side--${sTone}`}>
              {tSide(side, languageId)} {lotsTxt}
            </span>
          </div>
          <div className="hist-mt5-closed__line2">
            {fmtMt5Rate(openPx)}
            <span className="hist-mt5-closed__arrow"> → </span>
            {fmtMt5Rate(closePx)}
          </div>
        </div>
        <div className="hist-mt5-closed__right">
          <span className="hist-mt5-closed__pnl" style={pnl.color ? { color: pnl.color } : undefined}>
            {pnl.text}
          </span>
          <span className="hist-mt5-closed__time">{time}</span>
        </div>
      </div>
    </button>
  );
}

/**
 * Mobile MT5 Orders row — tap selects; details in bottom sheet.
 */
function Mt5OrderRow({ row, selected, onToggle }) {
  const { languageId } = useAppLanguage();
  const id = row.id ?? row.Id;
  const side = mt5OrderSideText(row);
  const sTone = sideTone(side);
  const symbol = historySymbolLabel(row);
  const setupTime = fmtMt5Time(
    row.timeSetup,
    row.TimeSetup,
    row.timeSetupText,
    row.TimeSetupText,
    row.addTime
  );
  const doneTime = fmtMt5Time(
    row.timeDone,
    row.TimeDone,
    row.timeDoneText,
    row.TimeDoneText,
    row.tradeDateTime
  );
  const time = doneTime !== "—" ? doneTime : setupTime;
  const status = orderStatusLabel(row);
  const volumeLine = mt5OrderVolumeLine(row);

  return (
    <button
      type="button"
      className={`hist-mt5-order hist-mt5-order--${sTone}${selected ? " is-selected" : ""}`}
      data-order-id={id}
      onClick={onToggle}
    >
      <div className="hist-mt5-order__main">
        <div className="hist-mt5-order__left">
          <div className="hist-mt5-order__line1">
            <span
              className="hist-mt5-order__symbol"
              title={symbol !== "—" ? symbol : undefined}
            >
              {symbol}
            </span>
            <span className={`hist-mt5-order__side hist-mt5-order__side--${sTone}`}>
              {tSide(side, languageId)}
            </span>
          </div>
          <div className="hist-mt5-order__line2">{volumeLine}</div>
        </div>
        <div className="hist-mt5-order__right">
          <span className="hist-mt5-order__status">{status}</span>
          <span className="hist-mt5-order__time">{time}</span>
        </div>
      </div>
    </button>
  );
}

/** Advanced MetaTrader-style history card (mobile). */
function HistAdvCard({
  symbol,
  side,
  meta,
  highlight,
  columns = [],
}) {
  const sideKey = sideTone(side);
  const primaryCols = columns.slice(0, 4);
  const extraCols = columns.slice(4);

  return (
    <article className={`hist-card hist-card--${sideKey}`}>
      <div className="hist-card__accent" aria-hidden />
      <div className="hist-card__top">
        <div className="hist-card__left">
          <div className="hist-card__symbol-row">
            <span className="hist-card__symbol">{symbol || "—"}</span>
            {side ? (
              <span className={`hist-card__badge hist-card__badge--${sideKey}`}>{side}</span>
            ) : null}
          </div>
          {meta ? (
            <div className="hist-card__time">
              <span className="hist-card__time-dot" aria-hidden />
              {meta}
            </div>
          ) : null}
        </div>
        {highlight ? (
          <div className={`hist-card__pnl hist-card__pnl--${highlight.tone || "flat"}`}>
            <span className="hist-card__pnl-label">{highlight.label || "P&L"}</span>
            <span
              className="hist-card__pnl-value"
              style={highlight.color ? { color: highlight.color } : undefined}
            >
              {highlight.value}
            </span>
          </div>
        ) : null}
      </div>

      {primaryCols.length > 0 && (
        <div className="hist-card__stats">
          {primaryCols.map((col) => (
            <div key={col.key} className="hist-card__stat">
              <span className="hist-card__stat-label">{col.label}</span>
              <span
                className="hist-card__stat-value"
                style={col.color ? { color: col.color } : undefined}
              >
                {col.value ?? "—"}
              </span>
            </div>
          ))}
        </div>
      )}

      {extraCols.length > 0 && (
        <div className="hist-card__extras">
          {extraCols.map((col) => (
            <span key={col.key} className="hist-card__chip">
              <em>{col.label}</em> {col.value ?? "—"}
            </span>
          ))}
        </div>
      )}
    </article>
  );
}

export default function History({
  initialTab = "deals",
  embedded = false,
  mt5 = false,
  startDate: startDateProp,
  endDate: endDateProp,
  visibleColumns,
  darkTheme = false,
} = {}) {
  const dispatch = useDispatch();
  const { t } = useAppLanguage();
  const { marginSummary } = useSelector((s) => s.margin || {});
  const accountBalance = Number(marginSummary?.balance ?? marginSummary?.Balance);
  const marketWatchList = useSelector((s) => s.marketWatch?.marketWatchList);
  const scriptIndexList = useSelector((s) => s.marketWatch?.scriptIndexList);
  const isMobileViewport = useMobileViewport();
  const loadSeq = useRef(0);
  const hasLoadedOnce = useRef(false);
  const expiryCacheRef = useRef({});
  const marketWatchListRef = useRef(marketWatchList);
  const scriptIndexListRef = useRef(scriptIndexList);
  marketWatchListRef.current = marketWatchList;
  scriptIndexListRef.current = scriptIndexList;
  const [lastChartSel, setLastChartSel] = useState(() => getLastChartSelection());
  const [fetchedSheetExpiry, setFetchedSheetExpiry] = useState("");

  useEffect(() => subscribeLastChartSelection(setLastChartSel), []);

  const firstMarketWatchKey = useMemo(() => {
    const rows = historyWatchlistRows(marketWatchList);
    for (const r of rows) {
      if (!r || typeof r !== "object") continue;
      const key = String(r.script || r.displayName || r.symbol || r.name || "").trim();
      if (key && !/^balance$/i.test(key)) return key;
    }
    return "";
  }, [marketWatchList]);

  /** Balance sheet: active if last selected OR 1st Market Watch exists. */
  const balanceActionsEnabled = Boolean(
    (lastChartSel?.symbol && !/^balance$/i.test(lastChartSel.symbol)) ||
      firstMarketWatchKey
  );
  const [tab, setTab] = useState(() => (VALID_TABS.has(initialTab) ? initialTab : "deals"));
  const [periodId, setPeriodId] = useState(() => readStoredHistoryPeriod());
  const [startDate, setStartDate] = useState(() => {
    if (startDateProp) return new Date(startDateProp);
    const range = periodRangeForId(readStoredHistoryPeriod());
    return range?.start || moment().subtract(3, "months").startOf("day").toDate();
  });
  const [endDate, setEndDate] = useState(() => {
    if (endDateProp) return new Date(endDateProp);
    const range = periodRangeForId(readStoredHistoryPeriod());
    return range?.end || moment().endOf("day").toDate();
  });
  /** Always-current From/To used by load (avoids stale closure / 1-month default). */
  const filterDatesRef = useRef({ start: startDate, end: endDate });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageNo, setPageNo] = useState(1);
  const [expandedDealId, setExpandedDealId] = useState(null);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [expandedClosedId, setExpandedClosedId] = useState(null);
  const [periodMenuOpen, setPeriodMenuOpen] = useState(false);
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const [sortId, setSortId] = useState("default");
  const [customStripVisible, setCustomStripVisible] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [customStart, setCustomStart] = useState(() =>
    moment().subtract(3, "months").startOf("day").toDate()
  );
  const [customEnd, setCustomEnd] = useState(() => new Date());
  const [customPickerTextMode, setCustomPickerTextMode] = useState(false);
  const [customYearPickerOpen, setCustomYearPickerOpen] = useState(false);
  const [customStartText, setCustomStartText] = useState("");
  const [customEndText, setCustomEndText] = useState("");
  const [customTextFocus, setCustomTextFocus] = useState("start");
  const lastInitialTab = useRef(initialTab);
  const appliedInitialPeriod = useRef(false);
  const customToday = useMemo(() => moment().startOf("day").toDate(), []);

  /** Step 1 — Custom Period: show From–To strip only (no calendar yet). */
  const activateCustomStrip = useCallback(() => {
    const s = startDate
      ? moment(startDate).startOf("day").toDate()
      : moment().subtract(3, "months").startOf("day").toDate();
    const e = endDate ? moment(endDate).startOf("day").toDate() : moment().startOf("day").toDate();
    setCustomStart(s);
    setCustomEnd(e);
    setCustomStartText(moment(s).format("DD/MM/YYYY"));
    setCustomEndText(moment(e).format("DD/MM/YYYY"));
    setCustomPickerTextMode(false);
    setCustomYearPickerOpen(false);
    setCustomTextFocus("start");
    setCustomOpen(false);
    setPeriodMenuOpen(false);
    setSortMenuOpen(false);
    setPeriodId("custom");
    try {
      localStorage.setItem(HISTORY_PERIOD_STORAGE, "custom");
    } catch {
      /* ignore */
    }
    setCustomStripVisible(true);
  }, [startDate, endDate]);

  /** Step 2 — tap From or To on strip → open single-date picker. */
  const openCustomDateField = useCallback(
    (which) => {
      const focus = which === "end" ? "end" : "start";
      const today = moment().startOf("day");
      const clampToday = (d) => {
        const m = moment(d).startOf("day");
        return (m.isAfter(today) ? today : m).toDate();
      };
      let s = clampToday(customStart || startDate || moment().startOf("day").toDate());
      let e = clampToday(customEnd || endDate || moment().startOf("day").toDate());
      // Standard range: To cannot be before From
      if (moment(e).isBefore(s, "day")) e = s;
      setCustomStart(s);
      setCustomEnd(e);
      setCustomStartText(moment(s).format("DD/MM/YYYY"));
      setCustomEndText(moment(e).format("DD/MM/YYYY"));
      setCustomTextFocus(focus);
      setCustomPickerTextMode(false);
      setCustomYearPickerOpen(false);
      setPeriodMenuOpen(false);
      setSortMenuOpen(false);
      setCustomStripVisible(true);
      setCustomOpen(true);
    },
    [customStart, customEnd, startDate, endDate]
  );

  const closeCustomRangePicker = useCallback(() => {
    setCustomOpen(false);
    setCustomPickerTextMode(false);
    setCustomYearPickerOpen(false);
  }, []);

  const setFilterRange = useCallback((start, end) => {
    const nextStart = moment(start).startOf("day").toDate();
    const nextEnd = moment(end).endOf("day").toDate();
    filterDatesRef.current = { start: nextStart, end: nextEnd };
    setStartDate(nextStart);
    setEndDate(nextEnd);
    return { start: nextStart, end: nextEnd };
  }, []);

  useEffect(() => {
    if (!VALID_TABS.has(initialTab)) return;
    if (lastInitialTab.current === initialTab) return;
    lastInitialTab.current = initialTab;
    setTab(initialTab);
    setPageNo(1);
  }, [initialTab]);

  useEffect(() => {
    if (startDateProp) {
      const end = endDateProp ? new Date(endDateProp) : filterDatesRef.current.end;
      setFilterRange(new Date(startDateProp), end);
    }
  }, [startDateProp, endDateProp, setFilterRange]);

  useEffect(() => {
    if (endDateProp && !startDateProp) {
      setFilterRange(filterDatesRef.current.start, new Date(endDateProp));
    }
  }, [endDateProp, startDateProp, setFilterRange]);

  const load = useCallback(
    async (page = 1, append = false, dateOverride = null) => {
      const ready = await waitForSessionUid();
      if (!ready) {
        setError("Not logged in");
        return;
      }
      const { sess, uid } = ready;
      const sessRoles = sess.roles || [sess.roleName || sess.role || "User"];
      const sessIsUser = String(sessRoles[0] || "").toLowerCase() === "user";

      const rangeStart = dateOverride?.start ?? filterDatesRef.current.start;
      const rangeEnd = dateOverride?.end ?? filterDatesRef.current.end;
      const startYmd = toHistoryFilterYmd(rangeStart);
      const endYmd = toHistoryFilterYmd(rangeEnd);
      const daySpan = Math.max(
        1,
        moment(endYmd).diff(moment(startYmd), "days") + 1
      );
      // Orders: one round-trip for the period (Total/Filled/Canceled). Other tabs stay paged.
      const pageSize =
        tab === "orders"
          ? 1000
          : Math.min(500, Math.max(mt5 || embedded ? 100 : 50, daySpan > 45 ? 200 : 100));

      const seq = ++loadSeq.current;
      setLoading(true);
      setError(null);
      if (!append) {
        setRows([]);
        setTotal(0);
      }
      try {
        const payload = {
          userId: Number(uid),
          pageNo: page,
          pageSize,
          segmentId: null,
          clientId: sessIsUser ? Number(uid) : null,
          symbol: null,
          searchText: "",
          startDate: startYmd,
          endDate: endYmd,
        };

        const endpoint = endpointForTab(tab);
        const part = await postHistoryWithRetry(endpoint, payload);
        if (seq !== loadSeq.current) return;

        let records = part.records || [];
        let totalRecords = part.total;

        if (tab === "orders") {
          const apiTotal = Number(part.total) || records.length;
          if (!append && records.length < apiTotal) {
            const lastPage = Math.min(20, Math.ceil(apiTotal / pageSize));
            const extra = [];
            for (let p = page + 1; p <= lastPage; p += 1) {
              extra.push(postHistoryWithRetry(endpoint, { ...payload, pageNo: p }));
            }
            const more = await Promise.all(extra);
            if (seq !== loadSeq.current) return;
            for (const block of more) {
              records = records.concat(block.records || []);
            }
          }
          records = records.filter((r) => !isHistoryOrderPending(r));
          totalRecords = records.length;
        }

        // Positions only: always merge Balance rows from Deals for the same period.
        // Deals attach path is reliable; closed/get-closed often omits cash on wide ranges.
        if (tab === "closed" && !append && page === 1) {
          try {
            const dealsPart = await postHistoryWithRetry("deals/get", {
              ...payload,
              pageNo: 1,
              pageSize: Math.min(500, Math.max(pageSize, 200)),
            });
            if (seq !== loadSeq.current) return;
            const fromDeals = (dealsPart.records || [])
              .filter(isBalanceDeal)
              .map(balanceDealAsClosedRow);
            if (fromDeals.length) {
              const nonBal = records.filter((r) => !isBalanceDeal(r));
              const byId = new Map();
              [...records.filter(isBalanceDeal), ...fromDeals].forEach((r) => {
                const id = String(r.positionId ?? r.PositionId ?? r.id ?? r.Id ?? "");
                if (id && !byId.has(id)) byId.set(id, r);
              });
              records = [...byId.values(), ...nonBal];
              totalRecords = (Number(part.total) || nonBal.length) + byId.size;
            }
          } catch {
            /* keep closed rows even if deals balance merge fails */
          }
        }

        setTotal(totalRecords);
        setPageNo(page);
        const normalized = (records || []).map(normalizeHistoryRow);
        setRows((prev) => (append ? [...prev, ...normalized] : normalized));
        hasLoadedOnce.current = true;
      } catch (e) {
        if (seq !== loadSeq.current) return;
        setError(
          e?.response?.data?.message ||
            e?.message ||
            "Failed to load. Deploy History SPs and restart API."
        );
        if (!append && !hasLoadedOnce.current) {
          setRows([]);
          setTotal(0);
        }
      } finally {
        if (seq === loadSeq.current) setLoading(false);
      }
    },
    [tab, mt5, embedded]
  );

  const applyPeriod = useCallback(
    (id, rangeOverride = null) => {
      const range = rangeOverride || periodRangeForId(id);
      if (!range) return;
      setPeriodId(id);
      try {
        localStorage.setItem(HISTORY_PERIOD_STORAGE, id);
      } catch {
        /* ignore */
      }
      setFilterRange(range.start, range.end);
      setPageNo(1);
      setPeriodMenuOpen(false);
      setCustomOpen(false);
      setCustomPickerTextMode(false);
      setCustomStripVisible(id === "custom");
    },
    [setFilterRange]
  );

  const confirmCustomRangePicker = useCallback(() => {
    const formats = ["DD/MM/YYYY", "MM/DD/YYYY", "YYYY-MM-DD", "MM-DD-YYYY", "DD-MM-YYYY"];
    const today = moment().startOf("day");
    const clampToToday = (d) => {
      const m = moment(d).startOf("day");
      return (m.isAfter(today) ? today : m).toDate();
    };
    let start = customStart;
    let end = customEnd;
    if (customPickerTextMode) {
      const raw = (customTextFocus === "end" ? customEndText : customStartText).trim();
      const parsed = moment(raw, formats, true);
      if (!parsed.isValid()) return;
      if (customTextFocus === "end") end = parsed.startOf("day").toDate();
      else start = parsed.startOf("day").toDate();
    }
    if (customTextFocus === "end" && !end && customStart) end = customStart;
    if (customTextFocus === "start" && !start && customEnd) start = customEnd;
    if (!start && !end) return;
    if (!start) start = end;
    if (!end) end = start;
    start = clampToToday(start);
    end = clampToToday(end);
    if (moment(end).isBefore(start, "day")) {
      const swap = start;
      start = moment(end).startOf("day").toDate();
      end = moment(swap).endOf("day").toDate();
    } else {
      start = moment(start).startOf("day").toDate();
      end = moment(end).endOf("day").toDate();
    }
    setCustomStart(start);
    setCustomEnd(end);
    setCustomStartText(moment(start).format("DD/MM/YYYY"));
    setCustomEndText(moment(end).format("DD/MM/YYYY"));
    setCustomOpen(false);
    setCustomPickerTextMode(false);
    setCustomYearPickerOpen(false);
    setCustomStripVisible(true);
    applyPeriod("custom", { start, end });
  }, [
    customStart,
    customEnd,
    customPickerTextMode,
    customStartText,
    customEndText,
    customTextFocus,
    applyPeriod,
  ]);

  // Mobile History: apply stored period once on first mount (skip when dock drives dates)
  useEffect(() => {
    if (embedded || mt5 || startDateProp || endDateProp) return;
    if (appliedInitialPeriod.current) return;
    appliedInitialPeriod.current = true;
    if (periodId === "custom") return;
    const range = periodRangeForId(periodId);
    if (range) setFilterRange(range.start, range.end);
  }, [embedded, mt5, startDateProp, endDateProp, periodId, setFilterRange]);

  // Top-bar calendar icon → open period menu
  useEffect(() => {
    if (embedded || mt5) return undefined;
    const onOpen = () => {
      setCustomOpen(false);
      setCustomPickerTextMode(false);
      setPeriodMenuOpen(true);
    };
    window.addEventListener(HISTORY_OPEN_PERIOD_EVENT, onOpen);
    return () => window.removeEventListener(HISTORY_OPEN_PERIOD_EVENT, onOpen);
  }, [embedded, mt5]);

  useEffect(() => {
    if (!periodMenuOpen && !customOpen && !sortMenuOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      setPeriodMenuOpen(false);
      setSortMenuOpen(false);
      closeCustomRangePicker();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [periodMenuOpen, customOpen, sortMenuOpen, closeCustomRangePicker]);

  const displayRows = useMemo(() => {
    const list = rows || [];
    const mobileMt5 = isMobileViewport && !embedded;
    let next;
    if (mobileMt5 && (!sortId || sortId === "default")) {
      next = [...list];
      if (tab === "closed") {
        // Positions: pin Balance rows at top (always visible for any period),
        // then closed trades newest-first (MT5). Fixes Last 3/6 Months / All History
        // where cash was buried under older pages of trades.
        const bal = [];
        const rest = [];
        for (const r of next) {
          if (isBalanceDeal(r)) bal.push(r);
          else rest.push(r);
        }
        bal.sort((a, b) => histRowTimeMs(b, true) - histRowTimeMs(a, true));
        rest.sort((a, b) => histRowTimeMs(b, true) - histRowTimeMs(a, true));
        next = bal.concat(rest);
      } else {
        next.sort(
          (a, b) => histRowTimeMs(a, false) - histRowTimeMs(b, false)
        );
      }
    } else {
      next = sortHistoryRows(list, sortId, tab);
    }
    return next;
  }, [rows, sortId, tab, isMobileViewport, embedded]);

  const nonCashRows = useMemo(
    () => (rows || []).filter((r) => !isBalanceDeal(r)),
    [rows]
  );

  useEffect(() => {
    load(1, false);
  }, [load, startDate, endDate, tab]);

  useEffect(() => {
    const onTz = () => load(1, false);
    window.addEventListener("fxb-broker-timezone", onTz);
    return () => window.removeEventListener("fxb-broker-timezone", onTz);
  }, [load]);

  const cols = visibleColumns;
  const useCards = isMobileViewport;
  /** Full-page mobile History — MT5 chrome (not dock embedded). */
  const isMobileMt5Page = useCards && !embedded;
  const showFullFilters = !embedded && !mt5 && !isMobileMt5Page;

  // Same account Balance as Trade (margin/summary) — not period DIY formula
  useEffect(() => {
    if (tab !== "deals" && tab !== "closed") return;
    dispatch(getMarginSummary());
  }, [dispatch, tab]);

  /**
   * Deals + closed Positions summary (MT5-style):
   * Profit, Deposit, Balance, Fee, Gross Profit / Credit, Withdrawal, Commission, Swap.
   */
  const pnlPeriodSummary = useMemo(() => {
    if (tab !== "deals" && tab !== "closed") return null;
    let profit = 0;
    let commission = 0;
    let deposit = 0;
    let withdrawal = 0;
    let fee = 0;
    let swap = 0;
    (rows || []).forEach((row) => {
      const p = pickDecimal(
        tab === "closed" ? row.realizedPnl : row.profit,
        tab === "closed" ? row.RealizedPnl : row.Profit
      );
      commission += getRowCommission(row) ?? 0;
      fee += pickDecimal(row.fee, row.Fee) ?? 0;
      swap += pickDecimal(row.swap, row.Swap, row.storage, row.Storage) ?? 0;

      // Cash/ledger rows are list "Balance" entries — not trading Profit.
      const tf = String(row.tradeFrom || row.TradeFrom || "").toLowerCase();
      const isWithdraw =
        tf.includes("withdraw") || tf.includes("pay-out") || tf.includes("payout");
      const isDeposit =
        tf.includes("deposit") || tf.includes("pay-in") || tf.includes("payin");
      if (isBalanceDeal(row) || isDeposit || isWithdraw) {
        const amt = balanceDealAmount(row);
        if (isWithdraw || (!isDeposit && amt < 0)) withdrawal += Math.abs(amt);
        else deposit += Math.abs(amt);
        return;
      }

      profit += p ?? 0;
    });
    const creditNum = Number(marginSummary?.credit ?? marginSummary?.Credit);
    const credit = Number.isFinite(creditNum) ? creditNum : 0;
    // Header Balance = account money left (MT5). List Balance row = the cash deal.
    const balance = Number.isFinite(accountBalance)
      ? accountBalance
      : deposit - withdrawal + profit + credit;
    return {
      profit,
      deposit,
      withdrawal,
      commission,
      fee,
      swap,
      balance,
      credit,
      grossProfit: profit,
    };
  }, [rows, tab, accountBalance, marginSummary]);

  /**
   * Period totals for applied From–To (all loaded done orders in that range).
   * Rejected is separate from Canceled so strip matches row status labels.
   */
  const ordersSummary = useMemo(() => {
    if (tab !== "orders") return null;
    let filled = 0;
    let canceled = 0;
    let rejected = 0;
    const list = rows || [];
    list.forEach((row) => {
      const key = orderStatusKey(row);
      if (key === "filled") filled += 1;
      else if (key === "canceled" || key === "expired") canceled += 1;
      else if (key === "rejected") rejected += 1;
    });
    return {
      filled,
      canceled,
      rejected,
      total: list.length,
    };
  }, [rows, tab]);

  const renderMt5DealsTable = () => (
    <table className="hist-table hist-table--mt5">
      <thead>
        <tr>
          {colOn(cols, "time") && histHead(t("time"))}
          {colOn(cols, "type") && histHead(t("type"))}
          {colOn(cols, "symbol") && histHead(t("symbol"))}
          {colOn(cols, "entry") && histHead(t("entry"))}
          {colOn(cols, "lots") && histHead(t("lots"), "num")}
          {colOn(cols, "qty") && histHead(t("qty"), "num")}
          {colOn(cols, "price") && histHead(t("price"), "num")}
          {colOn(cols, "commission") && histHead(t("commission"), "num")}
          {colOn(cols, "profit") && histHead(t("profit"), "num")}
          {colOn(cols, "positionId") && histHead(t("positionHash"))}
          {colOn(cols, "orderId") && histHead(t("orderHash"))}
        </tr>
      </thead>
      <tbody>
        {nonCashRows.map((row) => {
          const id = row.id ?? row.Id;
          const dealType = Number(row.dealType ?? row.DealType);
          const typeName = String(row.dealTypeName || row.DealTypeName || "").toLowerCase();
          const isBuy = dealType === 1 || typeName === "buy";
          const isSell = dealType === 2 || typeName === "sell";
          const typeColor = isBuy ? "#1e5eff" : isSell ? "#c62828" : undefined;
          const profitNum = row.profit == null || row.profit === "" ? null : Number(row.profit ?? row.Profit);
          const profitColor =
            profitNum == null || !Number.isFinite(profitNum)
              ? undefined
              : profitNum > 0
                ? "#1e5eff"
                : profitNum < 0
                  ? "#c62828"
                  : undefined;
          return (
            <tr key={id}>
              {colOn(cols, "time") && <td>{fmtTime(row.dealTime, row.DealTime, row.createdOn)}</td>}
              {colOn(cols, "type") && (
                <td style={typeColor ? { color: typeColor, fontWeight: 400 } : undefined}>
                  {fmt(row.dealTypeName || row.DealTypeName)}
                </td>
              )}
              {colOn(cols, "symbol") && <td>{historySymbolLabel(row)}</td>}
              {colOn(cols, "entry") && <td>{fmt(row.entryName || row.EntryName)}</td>}
              {colOn(cols, "lots") && (
                <td className="num">{fmt(row.volumeLots ?? row.VolumeLots ?? row.lot)}</td>
              )}
              {colOn(cols, "qty") && (
                <td className="num">{fmt(row.volumeQty ?? row.VolumeQty ?? row.qty)}</td>
              )}
              {colOn(cols, "price") && <td className="num">{fmt(row.price ?? row.Price)}</td>}
              {colOn(cols, "commission") && (
                <td className="num">{fmt(row.commission ?? row.Commission ?? 0)}</td>
              )}
              {colOn(cols, "profit") && (
                <td
                  className="num"
                  style={profitColor ? { color: profitColor, fontWeight: 400 } : undefined}
                >
                  {row.profit == null && row.Profit == null ? "—" : row.profit ?? row.Profit}
                </td>
              )}
              {colOn(cols, "positionId") && <td>{fmt(row.positionId ?? row.PositionId)}</td>}
              {colOn(cols, "orderId") && <td>{fmt(row.orderId ?? row.OrderId)}</td>}
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  const renderMt5OrdersTable = () => (
    <table className="hist-table hist-table--mt5">
      <thead>
        <tr>
          {colOn(cols, "setup") && histHead(t("setup"))}
          {colOn(cols, "done") && histHead(t("done"))}
          {colOn(cols, "state") && histHead(t("state"))}
          {colOn(cols, "side") && histHead("Buy/Sell")}
          {colOn(cols, "type") && histHead(t("type"))}
          {colOn(cols, "symbol") && histHead(t("symbol"))}
          {colOn(cols, "lots") && histHead(t("lots"), "num")}
          {colOn(cols, "qty") && histHead(t("qty"), "num")}
          {colOn(cols, "price") && histHead(t("price"), "num")}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const id = row.id ?? row.Id;
          const sideName = String(row.orderTypeName || row.OrderTypeName || "").toLowerCase();
          const isBuy = sideName.includes("buy");
          const isSell = sideName.includes("sell");
          const sideColor = isBuy ? "#1e5eff" : isSell ? "#c62828" : undefined;
          return (
            <tr key={id}>
              {colOn(cols, "setup") && (
                <td>{fmtTime(row.timeSetup, row.TimeSetup, row.timeSetupText, row.addTime)}</td>
              )}
              {colOn(cols, "done") && (
                <td>{fmtTime(row.timeDone, row.TimeDone, row.timeDoneText, row.tradeDateTime)}</td>
              )}
              {colOn(cols, "state") && (
                <td>{fmt(row.status || row.Status || row.stateName || row.StateName)}</td>
              )}
              {colOn(cols, "side") && (
                <td style={sideColor ? { color: sideColor, fontWeight: 400 } : undefined}>
                  {fmt(row.orderTypeName || row.OrderTypeName)}
                </td>
              )}
              {colOn(cols, "type") && (
                <td>{fmt(row.executionTypeName || row.ExecutionTypeName)}</td>
              )}
              {colOn(cols, "symbol") && <td>{historySymbolLabel(row)}</td>}
              {colOn(cols, "lots") && <td className="num">{fmt(row.lot ?? row.Lot)}</td>}
              {colOn(cols, "qty") && <td className="num">{fmt(row.qty ?? row.Qty)}</td>}
              {colOn(cols, "price") && <td className="num">{fmt(row.rate ?? row.Rate)}</td>}
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  const renderMt5ClosedTable = () => (
    <table className="hist-table hist-table--mt5">
      <thead>
        <tr>
          {colOn(cols, "open") && histHead(t("open"))}
          {colOn(cols, "close") && histHead(t("close"))}
          {colOn(cols, "type") && histHead(t("type"))}
          {colOn(cols, "symbol") && histHead(t("symbol"))}
          {colOn(cols, "lots") && histHead(t("lots"), "num")}
          {colOn(cols, "qty") && histHead(t("qty"), "num")}
          {colOn(cols, "openPrice") && histHead(t("open"), "num", t("openPriceLabel"))}
          {colOn(cols, "closePrice") && histHead(t("close"), "num", t("closePriceLabel"))}
          {colOn(cols, "commission") && histHead(t("commission"), "num")}
          {colOn(cols, "profit") && histHead(t("profit"), "num")}
          {colOn(cols, "positionId") && histHead(t("positionHash"))}
        </tr>
      </thead>
      <tbody>
        {nonCashRows.map((row) => {
          const id = row.id ?? row.Id ?? row.positionId ?? row.PositionId;
          const typeName = String(row.positionTypeName || row.type || "").toLowerCase();
          const isBuy = typeName === "buy" || Number(row.positionType) === 1;
          const isSell = typeName === "sell" || Number(row.positionType) === 2;
          const typeColor = isBuy ? "#1e5eff" : isSell ? "#c62828" : undefined;
          const pnl = row.realizedPnl == null ? null : Number(row.realizedPnl);
          const pnlColor =
            pnl == null || !Number.isFinite(pnl)
              ? undefined
              : pnl > 0
                ? "#1e5eff"
                : pnl < 0
                  ? "#c62828"
                  : undefined;
          return (
            <tr key={id}>
              {colOn(cols, "open") && <td>{fmtTime(row.timeCreate, row.openTime)}</td>}
              {colOn(cols, "close") && <td>{fmtTime(row.timeClose, row.closeTime)}</td>}
              {colOn(cols, "type") && (
                <td style={typeColor ? { color: typeColor, fontWeight: 400 } : undefined}>
                  {fmt(row.positionTypeName || row.type)}
                </td>
              )}
              {colOn(cols, "symbol") && <td>{historySymbolLabel(row)}</td>}
              {colOn(cols, "lots") && (
                <td className="num">{fmt(row.volumeLots ?? row.lot)}</td>
              )}
              {colOn(cols, "qty") && (
                <td className="num">{fmt(row.volumeQty ?? row.qty)}</td>
              )}
              {colOn(cols, "openPrice") && <td className="num">{fmt(row.priceOpen)}</td>}
              {colOn(cols, "closePrice") && <td className="num">{fmt(row.priceClose)}</td>}
              {colOn(cols, "commission") && <td className="num">{fmt(row.commission)}</td>}
              {colOn(cols, "profit") && (
                <td
                  className="num"
                  style={pnlColor ? { color: pnlColor, fontWeight: 400 } : undefined}
                >
                  {fmt(row.realizedPnl)}
                </td>
              )}
              {colOn(cols, "positionId") && <td>{fmt(row.positionId ?? id)}</td>}
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  const renderDealsTable = () => (
    <table className="hist-table">
      <thead>
        <tr>
          {histHead(t("time"))}
          {histHead(t("type"))}
          {histHead(t("symbol"))}
          {histHead(t("entry"))}
          {histHead(t("lots"), "num")}
          {histHead(t("qty"), "num")}
          {histHead(t("price"), "num")}
          {histHead(t("commission"), "num")}
          {histHead(t("profit"), "num")}
          {histHead(t("positionHash"))}
          {histHead(t("orderHash"))}
        </tr>
      </thead>
      <tbody>
        {nonCashRows.map((row) => {
          const dealType = Number(row.dealType);
          const typeName = String(row.dealTypeName || "").toLowerCase();
          const isBuy = dealType === 1 || typeName === "buy";
          const isSell = dealType === 2 || typeName === "sell";
          const typeColor = isBuy ? "#1e5eff" : isSell ? "#c62828" : undefined;
          const profitNum = row.profit == null || row.profit === "" ? null : Number(row.profit);
          const profitColor =
            profitNum == null || !Number.isFinite(profitNum)
              ? undefined
              : profitNum > 0
                ? "#1e5eff"
                : profitNum < 0
                  ? "#c62828"
                  : undefined;
          return (
            <tr key={row.id}>
              <td>{fmtTime(row.dealTime, row.createdOn)}</td>
              <td style={typeColor ? { color: typeColor, fontWeight: 400 } : undefined}>
                {fmt(row.dealTypeName)}
              </td>
              <td>{historySymbolLabel(row)}</td>
              <td>{fmt(row.entryName)}</td>
              <td className="num">{fmt(row.volumeLots)}</td>
              <td className="num">{fmt(row.volumeQty)}</td>
              <td className="num">{fmt(row.price)}</td>
              <td className="num">{fmt(row.commission)}</td>
              <td
                className="num"
                style={profitColor ? { color: profitColor, fontWeight: 400 } : undefined}
              >
                {row.profit == null ? "—" : row.profit}
              </td>
              <td>{fmt(row.positionId)}</td>
              <td>{fmt(row.orderId)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  const renderOrdersTable = () => (
    <table className="hist-table">
      <thead>
        <tr>
          {histHead(t("setup"))}
          {histHead(t("done"))}
          {histHead(t("state"))}
          {histHead("Buy/Sell")}
          {histHead(t("type"))}
          {histHead(t("symbol"))}
          {histHead(t("lots"), "num")}
          {histHead(t("qty"), "num")}
          {histHead(t("price"), "num")}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.id}>
            <td>{fmtTime(row.timeSetup, row.timeSetupText, row.addTime)}</td>
            <td>{fmtTime(row.timeDone, row.timeDoneText, row.tradeDateTime)}</td>
            <td>{fmt(row.status || row.stateName)}</td>
            <td>{fmt(row.orderTypeName)}</td>
            <td>{fmt(row.executionTypeName)}</td>
            <td>{historySymbolLabel(row)}</td>
            <td className="num">{fmt(row.lot)}</td>
            <td className="num">{fmt(row.qty)}</td>
            <td className="num">{fmt(row.rate)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderClosedTable = () => (
    <table className="hist-table">
      <thead>
        <tr>
          {histHead(t("open"))}
          {histHead(t("close"))}
          {histHead(t("type"))}
          {histHead(t("symbol"))}
          {histHead(t("lots"), "num")}
          {histHead(t("qty"), "num")}
          {histHead(t("open"), "num", t("openPriceLabel"))}
          {histHead(t("close"), "num", t("closePriceLabel"))}
          {histHead(t("commission"), "num")}
          {histHead(t("profit"), "num")}
          {histHead(t("positionHash"))}
        </tr>
      </thead>
      <tbody>
        {nonCashRows.map((row) => {
          const typeName = String(row.positionTypeName || row.type || "").toLowerCase();
          const isBuy = typeName === "buy" || Number(row.positionType) === 1;
          const isSell = typeName === "sell" || Number(row.positionType) === 2;
          const typeColor = isBuy ? "#1e5eff" : isSell ? "#c62828" : undefined;
          const pnl = row.realizedPnl == null || row.realizedPnl === "" ? null : Number(row.realizedPnl);
          const pnlColor =
            pnl == null || !Number.isFinite(pnl)
              ? undefined
              : pnl > 0
                ? "#1e5eff"
                : pnl < 0
                  ? "#c62828"
                  : undefined;
          return (
            <tr key={row.id || row.positionId}>
              <td>{fmtTime(row.timeCreate, row.openTime)}</td>
              <td>{fmtTime(row.timeClose, row.closeTime)}</td>
              <td style={typeColor ? { color: typeColor, fontWeight: 400 } : undefined}>
                {fmt(row.positionTypeName || row.type)}
              </td>
              <td>{historySymbolLabel(row)}</td>
              <td className="num">{fmt(row.volumeLots ?? row.lot)}</td>
              <td className="num">{fmt(row.volumeQty ?? row.qty)}</td>
              <td className="num">{fmt(row.priceOpen)}</td>
              <td className="num">{fmt(row.priceClose)}</td>
              <td className="num">{fmt(row.commission)}</td>
              <td
                className="num"
                style={pnlColor ? { color: pnlColor, fontWeight: 400 } : undefined}
              >
                {fmt(row.realizedPnl)}
              </td>
              <td>{fmt(row.positionId)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  const renderMt5Deals = () => (
    <div className="hist-adv-list" role="list">
      {rows.map((row) => {
        const id = row.id ?? row.Id;
        const dealType = Number(row.dealType ?? row.DealType);
        const typeName = String(row.dealTypeName || row.DealTypeName || "").toLowerCase();
        const isBuy = dealType === 1 || typeName === "buy";
        const isSell = dealType === 2 || typeName === "sell";
        const side = isBuy ? "Buy" : isSell ? "Sell" : fmt(row.dealTypeName || row.DealTypeName);
        const profitRaw = row.profit ?? row.Profit;
        const profitNum = profitRaw == null || profitRaw === "" ? null : Number(profitRaw);
        const pnl = profitTone(profitNum);
        const entry = String(row.entryName || row.EntryName || "").trim() || "—";
        const symbol = historySymbolLabel(row);
        const time = fmtTime(row.dealTime, row.DealTime, row.createdOn);
        const columns = [
          colOn(cols, "time") && { key: "time", label: "Time", value: time },
          colOn(cols, "entry") && { key: "entry", label: "Entry", value: entry },
          colOn(cols, "lots") && {
            key: "lots",
            label: "Lots",
            value: fmt(row.volumeLots ?? row.VolumeLots ?? row.lot),
          },
          colOn(cols, "qty") && {
            key: "qty",
            label: "Qty",
            value: fmt(row.volumeQty ?? row.VolumeQty ?? row.qty),
          },
          colOn(cols, "price") && { key: "price", label: "Price", value: fmt(row.price ?? row.Price) },
          colOn(cols, "commission") && {
            key: "comm",
            label: "Comm.",
            value: fmt(row.commission ?? row.Commission ?? 0),
          },
          colOn(cols, "positionId") && {
            key: "positionId",
            label: "Position#",
            value: fmt(row.positionId ?? row.PositionId),
          },
          colOn(cols, "orderId") && {
            key: "orderId",
            label: "Order#",
            value: fmt(row.orderId ?? row.OrderId),
          },
        ].filter(Boolean);

        return (
          <div key={id} role="listitem">
            <HistAdvCard
              symbol={symbol}
              side={colOn(cols, "type") ? side : null}
              meta={time !== "—" ? time : null}
              highlight={
                colOn(cols, "profit")
                  ? { label: t("profit"), value: pnl.text, color: pnl.color, tone: pnl.tone }
                  : null
              }
              columns={columns}
            />
          </div>
        );
      })}
    </div>
  );

  const renderMt5Orders = () => (
    <div className="hist-adv-list" role="list">
      {rows.map((row) => {
        const id = row.id ?? row.Id;
        const side = fmt(row.orderTypeName || row.OrderTypeName);
        const symbol = historySymbolLabel(row);
        const setup = fmtTime(row.timeSetup, row.TimeSetup, row.timeSetupText, row.addTime);
        const done = fmtTime(row.timeDone, row.TimeDone, row.timeDoneText, row.tradeDateTime);
        const state = fmt(row.status || row.Status || row.stateName || row.StateName);
        const columns = [
          colOn(cols, "setup") && { key: "setup", label: "Setup", value: setup },
          colOn(cols, "done") && { key: "done", label: "Done", value: done },
          colOn(cols, "state") && { key: "state", label: "State", value: state },
          colOn(cols, "type") && {
            key: "type",
            label: "Type",
            value: fmt(row.executionTypeName || row.ExecutionTypeName),
          },
          colOn(cols, "lots") && { key: "lots", label: "Lots", value: fmt(row.lot ?? row.Lot) },
          colOn(cols, "qty") && { key: "qty", label: "Qty", value: fmt(row.qty ?? row.Qty) },
          colOn(cols, "price") && { key: "price", label: "Price", value: fmt(row.rate ?? row.Rate) },
        ].filter(Boolean);

        return (
          <div key={id} role="listitem">
            <HistAdvCard
              symbol={symbol}
              side={colOn(cols, "side") ? side : null}
              meta={state !== "—" ? state : setup !== "—" ? setup : null}
              columns={columns}
            />
          </div>
        );
      })}
    </div>
  );

  const renderMt5Closed = () => (
    <div className="hist-adv-list" role="list">
      {rows.map((row) => {
        const id = row.id ?? row.Id ?? row.positionId ?? row.PositionId;
        const typeName = String(row.positionTypeName || row.type || "").toLowerCase();
        const isBuy = typeName === "buy" || Number(row.positionType) === 1;
        const isSell = typeName === "sell" || Number(row.positionType) === 2;
        const side = isBuy ? "Buy" : isSell ? "Sell" : fmt(row.positionTypeName || row.type);
        const pnlRaw = row.realizedPnl;
        const pnlNum = pnlRaw == null || pnlRaw === "" ? null : Number(pnlRaw);
        const pnl = profitTone(pnlNum);
        const openT = fmtTime(row.timeCreate, row.openTime);
        const closeT = fmtTime(row.timeClose, row.closeTime);
        const columns = [
          colOn(cols, "open") && { key: "open", label: "Open", value: openT },
          colOn(cols, "close") && { key: "close", label: "Close", value: closeT },
          colOn(cols, "lots") && {
            key: "lots",
            label: "Lots",
            value: fmt(row.volumeLots ?? row.lot),
          },
          colOn(cols, "qty") && {
            key: "qty",
            label: "Qty",
            value: fmt(row.volumeQty ?? row.qty),
          },
          colOn(cols, "openPrice") && { key: "op", label: "Open", value: fmt(row.priceOpen) },
          colOn(cols, "closePrice") && { key: "cp", label: "Close", value: fmt(row.priceClose) },
          colOn(cols, "commission") && {
            key: "comm",
            label: "Comm.",
            value: fmt(row.commission),
          },
          colOn(cols, "positionId") && {
            key: "positionId",
            label: "Position#",
            value: fmt(row.positionId ?? id),
          },
        ].filter(Boolean);

        return (
          <div key={id} role="listitem">
            <HistAdvCard
              symbol={historySymbolLabel(row)}
              side={colOn(cols, "type") ? side : null}
              meta={closeT !== "—" ? closeT : openT !== "—" ? openT : null}
              highlight={
                colOn(cols, "profit")
                  ? { label: t("profit"), value: pnl.text, color: pnl.color, tone: pnl.tone }
                  : null
              }
              columns={columns}
            />
          </div>
        );
      })}
    </div>
  );

  /** Mobile full-page Deals — MT5 list (our deal fields). */
  const renderMt5DealsMobile = () => (
    <div className="hist-mt5-deal-list" role="list">
      {displayRows.map((row) => {
        const id = row.id ?? row.Id;
        return (
          <div key={id} role="listitem">
            <Mt5DealRow
              row={row}
              selected={expandedDealId === id}
              onToggle={() => {
                setExpandedOrderId(null);
                setExpandedClosedId(null);
                setExpandedDealId((prev) => (prev === id ? null : id));
              }}
            />
          </div>
        );
      })}
    </div>
  );

  const renderDealsCards = () => {
    if (isMobileMt5Page) return renderMt5DealsMobile();
    return (
      <div className="hist-adv-list" role="list">
        {nonCashRows.map((row) => {
          const dealType = Number(row.dealType);
          const typeName = String(row.dealTypeName || "").toLowerCase();
          const isBuy = dealType === 1 || typeName === "buy";
          const isSell = dealType === 2 || typeName === "sell";
          const side = isBuy ? "Buy" : isSell ? "Sell" : fmt(row.dealTypeName);
          const profitNum = row.profit == null || row.profit === "" ? null : Number(row.profit);
          const pnl = profitTone(profitNum);
          const time = fmtTime(row.dealTime, row.createdOn);
          return (
            <div key={row.id} role="listitem">
              <HistAdvCard
                symbol={historySymbolLabel(row)}
                side={side}
                meta={time}
                highlight={{ label: t("profit"), value: pnl.text, color: pnl.color, tone: pnl.tone }}
                columns={[
                  { key: "entry", label: "Entry", value: fmt(row.entryName) },
                  { key: "lots", label: "Lots", value: fmt(row.volumeLots) },
                  { key: "qty", label: "Qty", value: fmt(row.volumeQty) },
                  { key: "price", label: "Price", value: fmt(row.price) },
                  { key: "comm", label: "Comm.", value: fmt(row.commission) },
                  { key: "pos", label: "Pos#", value: fmt(row.positionId) },
                  { key: "ord", label: "Order#", value: fmt(row.orderId) },
                ]}
              />
            </div>
          );
        })}
      </div>
    );
  };

  /** Mobile full-page Orders — MT5 list (our order history fields). */
  const renderMt5OrdersMobile = () => (
    <div className="hist-mt5-order-list" role="list">
      {displayRows.map((row) => {
        const id = row.id ?? row.Id;
        return (
          <div key={id} role="listitem">
            <Mt5OrderRow
              row={row}
              selected={expandedOrderId === id}
              onToggle={() => {
                setExpandedDealId(null);
                setExpandedClosedId(null);
                setExpandedOrderId((prev) => (prev === id ? null : id));
              }}
            />
          </div>
        );
      })}
    </div>
  );

  const renderOrdersCards = () => {
    if (isMobileMt5Page) return renderMt5OrdersMobile();
    return (
      <div className="hist-adv-list" role="list">
        {rows.map((row) => {
          const side = fmt(row.orderTypeName);
          const setup = fmtTime(row.timeSetup, row.timeSetupText, row.addTime);
          const done = fmtTime(row.timeDone, row.timeDoneText, row.tradeDateTime);
          const state = fmt(row.status || row.stateName);
          return (
            <div key={row.id} role="listitem">
              <HistAdvCard
                symbol={historySymbolLabel(row)}
                side={side}
                meta={state !== "—" ? state : setup}
                columns={[
                  { key: "setup", label: "Setup", value: setup },
                  { key: "done", label: "Done", value: done },
                  { key: "type", label: "Type", value: fmt(row.executionTypeName) },
                  { key: "lots", label: "Lots", value: fmt(row.lot) },
                  { key: "qty", label: "Qty", value: fmt(row.qty) },
                  { key: "price", label: "Price", value: fmt(row.rate) },
                ]}
              />
            </div>
          );
        })}
      </div>
    );
  };

  /** Mobile full-page closed Positions — MT5 list. */
  const renderMt5ClosedMobile = () => (
    <div className="hist-mt5-closed-list" role="list">
      {displayRows.map((row, index) => {
        const id = row.id ?? row.Id ?? row.positionId ?? row.PositionId;
        const bal = isBalanceDeal(row);
        const key = bal ? `bal-${id ?? index}` : `pos-${id ?? index}`;
        return (
          <div key={key} role="listitem">
            <Mt5ClosedRow
              row={row}
              selected={expandedClosedId === id}
              onToggle={() => {
                setExpandedDealId(null);
                setExpandedOrderId(null);
                setExpandedClosedId((prev) => (prev === id ? null : id));
              }}
            />
          </div>
        );
      })}
    </div>
  );

  const renderClosedCards = () => {
    if (isMobileMt5Page) return renderMt5ClosedMobile();
    return (
      <div className="hist-adv-list" role="list">
        {nonCashRows.map((row) => {
          const typeName = String(row.positionTypeName || row.type || "").toLowerCase();
          const isBuy = typeName === "buy" || Number(row.positionType) === 1;
          const isSell = typeName === "sell" || Number(row.positionType) === 2;
          const side = isBuy ? "Buy" : isSell ? "Sell" : fmt(row.positionTypeName || row.type);
          const pnlNum =
            row.realizedPnl == null || row.realizedPnl === "" ? null : Number(row.realizedPnl);
          const pnl = profitTone(pnlNum);
          const openT = fmtTime(row.timeCreate, row.openTime);
          const closeT = fmtTime(row.timeClose, row.closeTime);
          return (
            <div key={row.id || row.positionId} role="listitem">
              <HistAdvCard
                symbol={historySymbolLabel(row)}
                side={side}
                meta={closeT !== "—" ? closeT : openT}
                highlight={{ label: t("profit"), value: pnl.text, color: pnl.color, tone: pnl.tone }}
                columns={[
                  { key: "open", label: "Open", value: openT },
                  { key: "close", label: "Close", value: closeT },
                  { key: "lots", label: "Lots", value: fmt(row.volumeLots ?? row.lot) },
                  { key: "qty", label: "Qty", value: fmt(row.volumeQty ?? row.qty) },
                  { key: "op", label: "Open Px", value: fmt(row.priceOpen) },
                  { key: "cp", label: "Close Px", value: fmt(row.priceClose) },
                  { key: "comm", label: "Comm.", value: fmt(row.commission) },
                  { key: "pos", label: "Pos#", value: fmt(row.positionId) },
                ]}
              />
            </div>
          );
        })}
      </div>
    );
  };

  const selectedSheetModel = useMemo(() => {
    if (!isMobileMt5Page) return null;
    if (tab === "deals" && expandedDealId != null) {
      const row = displayRows.find((r) => (r.id ?? r.Id) === expandedDealId);
      return buildHistMt5Sheet("deal", row, marketWatchList, scriptIndexList);
    }
    if (tab === "orders" && expandedOrderId != null) {
      const row = displayRows.find((r) => (r.id ?? r.Id) === expandedOrderId);
      return buildHistMt5Sheet("order", row, marketWatchList, scriptIndexList);
    }
    if (tab === "closed" && expandedClosedId != null) {
      const row = displayRows.find(
        (r) => (r.id ?? r.Id ?? r.positionId ?? r.PositionId) === expandedClosedId
      );
      return buildHistMt5Sheet("closed", row, marketWatchList, scriptIndexList);
    }
    return null;
  }, [
    isMobileMt5Page,
    tab,
    displayRows,
    expandedDealId,
    expandedOrderId,
    expandedClosedId,
    marketWatchList,
    scriptIndexList,
  ]);

  useEffect(() => {
    if (!isMobileMt5Page || !selectedSheetModel) {
      setFetchedSheetExpiry("");
      return undefined;
    }
    // MT5 deal popup has no expiry line; Orders/closed still fetch it.
    if (
      selectedSheetModel.kind === "deal" ||
      selectedSheetModel.expiry ||
      selectedSheetModel.symbol === "Balance"
    ) {
      setFetchedSheetExpiry("");
      return undefined;
    }
    const row = selectedSheetModel.row;
    const sym = historySymbolLabel(row);
    if (!sym || sym === "—") {
      setFetchedSheetExpiry("");
      return undefined;
    }
    let seg = Number(row?.segmentId ?? row?.SegmentId);
    if (!Number.isFinite(seg) || seg <= 0) {
      const matched = [
        ...historyWatchlistRows(marketWatchListRef.current),
        ...historyWatchlistRows(scriptIndexListRef.current),
      ];
      const wantNames = historyWantNames(row);
      for (const w of matched) {
        if (!w || typeof w !== "object") continue;
        const wNames = [
          w.symbol,
          w.script,
          w.displayName,
          w.Symbol,
          w.Script,
          w.DisplayName,
          w.name,
        ];
        if (!wNames.some((n) => historyWatchNameHits(wantNames, n))) continue;
        const wSeg = Number(w.segmentId ?? w.SegmentId);
        if (Number.isFinite(wSeg) && wSeg > 0) {
          seg = wSeg;
          break;
        }
      }
    }
    if (!Number.isFinite(seg) || seg <= 0) return undefined;
    const key = `${seg}|${sym.toUpperCase()}`;
    const cached = expiryCacheRef.current[key];
    if (cached) {
      setFetchedSheetExpiry(cached);
      return undefined;
    }
    let cancelled = false;
    setFetchedSheetExpiry("");
    api
      .get(`market-watch/get-expiry-dates-by-symbol/${seg}/${encodeURIComponent(sym)}`)
      .then((res) => {
        if (cancelled) return;
        const label = pickHistoryExpiryFromList(extractHistoryExpiryApiList(res));
        if (!label) return;
        expiryCacheRef.current[key] = label;
        setFetchedSheetExpiry(label);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [
    isMobileMt5Page,
    selectedSheetModel?.kind,
    selectedSheetModel?.id,
    selectedSheetModel?.symbol,
    selectedSheetModel?.expiry,
  ]);

  const displaySheetModel = useMemo(() => {
    if (!selectedSheetModel) return null;
    if (
      selectedSheetModel.kind === "deal" ||
      selectedSheetModel.expiry ||
      !fetchedSheetExpiry
    ) {
      return selectedSheetModel;
    }
    return { ...selectedSheetModel, expiry: fetchedSheetExpiry };
  }, [selectedSheetModel, fetchedSheetExpiry]);

  const closeHistSheet = useCallback(() => {
    setExpandedDealId(null);
    setExpandedOrderId(null);
    setExpandedClosedId(null);
  }, []);

  /* Hide mobile footer while History detail sheet is open (MT5). */
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const open = Boolean(isMobileMt5Page && selectedSheetModel);
    document.body.classList.toggle("hist-mt5-sheet-open", open);
    return () => document.body.classList.remove("hist-mt5-sheet-open");
  }, [isMobileMt5Page, selectedSheetModel]);

  const onHistSheetChart = useCallback((model) => {
    if (typeof window === "undefined") return;
    const useLast = Boolean(model?.useLastSelectedSymbol) || model?.symbol === "Balance";
    if (useLast && !balanceActionsEnabled) return;
    const symbol = useLast ? "" : String(model?.chartSymbol || "").trim();
    if (!useLast && !symbol) return;
    window.dispatchEvent(
      new CustomEvent("fx-history-open-chart", {
        detail: {
          symbol,
          row: useLast ? null : model.row,
          useLastSelected: useLast,
        },
      })
    );
    closeHistSheet();
  }, [closeHistSheet, balanceActionsEnabled]);

  const onHistSheetNewOrder = useCallback((model) => {
    if (typeof window === "undefined") return;
    const useLast = Boolean(model?.useLastSelectedSymbol) || model?.symbol === "Balance";
    if (useLast && !balanceActionsEnabled) return;
    const symbol = useLast ? "" : String(model?.chartSymbol || "").trim();
    if (!useLast && !symbol) return;
    window.dispatchEvent(
      new CustomEvent("fx-history-new-order", {
        detail: {
          symbol,
          row: useLast ? null : model.row,
          useLastSelected: useLast,
        },
      })
    );
    closeHistSheet();
  }, [closeHistSheet, balanceActionsEnabled]);

  const renderTable = () => {
    if (loading && !rows.length) {
      return <div className="hist-empty">{t("loading")}</div>;
    }
    if (!loading && rows.length === 0) {
      return (
        <div className="hist-empty">
          {tab === "orders" ? "No completed orders in this period." : "No records in this period"}
        </div>
      );
    }

    // Mobile: advanced cards · Web/desktop: tables
    if (mt5 || embedded) {
      if (useCards) {
        if (tab === "deals") return renderMt5Deals();
        if (tab === "orders") return renderMt5Orders();
        return renderMt5Closed();
      }
      if (tab === "deals") return renderMt5DealsTable();
      if (tab === "orders") return renderMt5OrdersTable();
      return renderMt5ClosedTable();
    }

    if (useCards) {
      if (tab === "deals") return renderDealsCards();
      if (tab === "orders") return renderOrdersCards();
      return renderClosedCards();
    }

    if (tab === "deals") return renderDealsTable();
    if (tab === "orders") return renderOrdersTable();
    return renderClosedTable();
  };

  return (
    <div
      className={`hist-page hist-page--adv${embedded || mt5 ? " hist-page--embedded hist-page--mt5" : ""}${
        darkTheme ? " hist-page--dark" : ""
      }${useCards ? " hist-page--mobile-cards" : " hist-page--desktop-table"}${
        isMobileMt5Page ? " hist-page--mt5-mobile" : ""
      }`}
    >
      {isMobileMt5Page && (
        <>
          <div className="hist-mt5-tabs" role="tablist" aria-label={t("history")}>
            <div className="hist-mt5-tabs__list">
              {MT5_TABS.map((tabItem) => (
                <button
                  key={tabItem.id}
                  type="button"
                  role="tab"
                  aria-selected={tab === tabItem.id}
                  className={`hist-mt5-tabs__btn${tab === tabItem.id ? " is-active" : ""}`}
                  onClick={() => {
                    setTab(tabItem.id);
                    setPageNo(1);
                    setExpandedDealId(null);
                    setExpandedOrderId(null);
                    setExpandedClosedId(null);
                  }}
                >
                  {tabItem.id === "closed" ? t("positions") : tabItem.id === "orders" ? t("orders") : t("deals")}
                </button>
              ))}
            </div>
            <div className="hist-mt5-tabs__actions">
              <button
                type="button"
                className="hist-mt5-tabs__icon-btn"
                aria-label={t("sortBy")}
                title={t("sortBy")}
                onClick={() => {
                  setPeriodMenuOpen(false);
                  setCustomOpen(false);
                  setSortMenuOpen(true);
                }}
              >
                {/* MT5 sort: ↕ + three lines */}
                <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
                  <path
                    d="M6 8.2L8.2 5.8 10.4 8.2M8.2 5.8v12.4M6 15.8l2.2 2.4 2.2-2.4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.65"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M13.2 7h7.2M13.2 12h5.4M13.2 17h3.4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.65"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
              <button
                type="button"
                className="hist-mt5-tabs__icon-btn"
                aria-label={t("period")}
                title={t("period")}
                onClick={() => {
                  setSortMenuOpen(false);
                  setCustomOpen(false);
                  setPeriodMenuOpen(true);
                }}
              >
                {/* MT5 period: stopwatch */}
                <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
                  <path
                    d="M10.2 3.4h3.6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                  />
                  <circle
                    cx="12"
                    cy="13.2"
                    r="7.1"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                  />
                  <path
                    d="M12 13.2V8.6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
          </div>
          {(tab === "deals" || tab === "closed") && pnlPeriodSummary && (
            <div
              className="hist-mt5-summary hist-mt5-summary--grid"
              aria-label={tab === "closed" ? "Positions summary" : "Deals summary"}
            >
              <div className="hist-mt5-summary__col">
                {[
                  {
                    key: "profit",
                    label: `${t("profit")}:`,
                    value: fmtMt5Num(pnlPeriodSummary.profit),
                    tone:
                      pnlPeriodSummary.profit > 0
                        ? "up"
                        : pnlPeriodSummary.profit < 0
                          ? "down"
                          : "",
                  },
                  {
                    key: "deposit",
                    label: `${t("deposit")}:`,
                    value: fmtMt5Num(pnlPeriodSummary.deposit),
                  },
                  {
                    key: "balance",
                    label: `${t("balance")}:`,
                    value: fmtMt5Num(pnlPeriodSummary.balance),
                    bold: true,
                  },
                  {
                    key: "fee",
                    label: `${t("fee")}:`,
                    value: fmtMt5Num(pnlPeriodSummary.fee),
                  },
                  {
                    key: "gross",
                    label: `${t("grossProfit")}:`,
                    value: fmtMt5Num(pnlPeriodSummary.grossProfit),
                    tone:
                      pnlPeriodSummary.grossProfit > 0
                        ? "up"
                        : pnlPeriodSummary.grossProfit < 0
                          ? "down"
                          : "",
                  },
                ].map((cell) => (
                  <div key={cell.key} className="hist-mt5-summary__cell">
                    <span className="hist-mt5-summary__label">{cell.label}</span>
                    <span
                      className={`hist-mt5-summary__value${cell.bold ? " is-bold" : ""}${
                        cell.tone === "up" ? " is-up" : cell.tone === "down" ? " is-down" : ""
                      }`}
                    >
                      {cell.value}
                    </span>
                  </div>
                ))}
              </div>
              <div className="hist-mt5-summary__col">
                {[
                  {
                    key: "credit",
                    label: `${t("credit")}:`,
                    value: fmtMt5Num(pnlPeriodSummary.credit),
                  },
                  {
                    key: "withdrawal",
                    label: `${t("withdrawal")}:`,
                    value: fmtMt5Num(pnlPeriodSummary.withdrawal),
                  },
                  {
                    key: "commission",
                    label: `${t("commission")}:`,
                    value: fmtMt5Num(pnlPeriodSummary.commission),
                  },
                  {
                    key: "swap",
                    label: `${t("swap")}:`,
                    value: fmtMt5Num(pnlPeriodSummary.swap),
                  },
                ].map((cell) => (
                  <div key={cell.key} className="hist-mt5-summary__cell">
                    <span className="hist-mt5-summary__label">{cell.label}</span>
                    <span className="hist-mt5-summary__value">{cell.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {tab === "orders" && ordersSummary && (
            <div className="hist-mt5-summary hist-mt5-summary--grid" aria-label="Orders summary">
              <div className="hist-mt5-summary__col">
                {[
                  { key: "total", label: `${t("total")}:`, value: ordersSummary.total },
                  { key: "canceled", label: `${t("canceled")}:`, value: ordersSummary.canceled },
                ].map((cell) => (
                  <div key={cell.key} className="hist-mt5-summary__cell">
                    <span className="hist-mt5-summary__label">{cell.label}</span>
                    <span className="hist-mt5-summary__value">{cell.value}</span>
                  </div>
                ))}
              </div>
              <div className="hist-mt5-summary__col">
                {[
                  { key: "filled", label: `${t("filled")}:`, value: ordersSummary.filled },
                  { key: "rejected", label: `${t("rejected")}:`, value: ordersSummary.rejected },
                ].map((cell) => (
                  <div key={cell.key} className="hist-mt5-summary__cell">
                    <span className="hist-mt5-summary__label">{cell.label}</span>
                    <span className="hist-mt5-summary__value">{cell.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {showFullFilters && (
        <>
          <div className="hist-page__head">
            <h3 className="hist-page__title">{t("history")}</h3>
            <span className="hist-page__total">Total: {total}</span>
          </div>
          <div className="hist-tabs">
            {TABS.map((tabItem) => (
              <button
                key={tabItem.id}
                type="button"
                className={`hist-tabs__btn${tab === tabItem.id ? " is-active" : ""}`}
                onClick={() => {
                  setTab(tabItem.id);
                  setPageNo(1);
                }}
              >
                {tabItem.id === "closed" ? t("closedPositions") : tabItem.id === "orders" ? t("orders") : t("deals")}
              </button>
            ))}
          </div>
          <div className="hist-filters">
            <DatePicker
              selected={startDate}
              onChange={(d) => {
                if (!d) return;
                const end =
                  endDate && moment(d).isAfter(endDate, "day") ? d : endDate;
                setFilterRange(d, end || d);
              }}
              selectsStart
              startDate={startDate}
              endDate={endDate}
              maxDate={endDate || new Date()}
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              dateFormat={UI_DATEPICKER_FORMAT}
              className="form-control"
            />
            <DatePicker
              selected={endDate}
              onChange={(d) => {
                if (!d) return;
                const start =
                  startDate && moment(d).isBefore(startDate, "day")
                    ? d
                    : startDate;
                setFilterRange(start || d, d);
              }}
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate}
              maxDate={new Date()}
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              dateFormat={UI_DATEPICKER_FORMAT}
              className="form-control"
            />
            <button
              type="button"
              className="hist-filters__find"
              onClick={() => {
                const next = setFilterRange(startDate, endDate);
                load(1, false, next);
              }}
            >
              Find
            </button>
          </div>
        </>
      )}

      {error && <div className="hist-error">{error}</div>}

      <div
        className={`hist-scroll${displaySheetModel ? " hist-scroll--sheet-open" : ""}`}
      >
        {renderTable()}
      </div>

      {isMobileMt5Page && displaySheetModel
        ? createPortal(
            <HistMt5DetailSheet
              model={displaySheetModel}
              onClose={closeHistSheet}
              onChart={onHistSheetChart}
              onNewOrder={onHistSheetNewOrder}
              actionsEnabled={
                displaySheetModel.useLastSelectedSymbol ||
                displaySheetModel.symbol === "Balance"
                  ? balanceActionsEnabled
                  : true
              }
            />,
            document.body
          )
        : null}

      {periodMenuOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="hist-mt5-sheet-menu-overlay"
            role="presentation"
            onClick={() => setPeriodMenuOpen(false)}
          >
            <div
              className="hist-mt5-sheet-menu hist-mt5-sheet-menu--period"
              role="menu"
              aria-label={t("period")}
              onClick={(e) => e.stopPropagation()}
            >
              {HISTORY_PERIODS.map((p) => {
                const rangeTxt = p.id === "custom" || p.id === "all" ? "" : formatPeriodMenuRange(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    role="menuitemradio"
                    aria-checked={periodId === p.id}
                    className={`hist-mt5-sheet-menu__item${periodId === p.id ? " is-active" : ""}`}
                    onClick={() => {
                      if (p.id === "custom") {
                        activateCustomStrip();
                        return;
                      }
                      applyPeriod(p.id);
                    }}
                  >
                    <span className="hist-mt5-sheet-menu__text">
                      <span className="hist-mt5-sheet-menu__label">{t(p.labelKey)}</span>
                      {rangeTxt ? (
                        <span className="hist-mt5-sheet-menu__range">{rangeTxt}</span>
                      ) : null}
                    </span>
                    {periodId === p.id ? (
                      <span className="hist-mt5-sheet-menu__radio" aria-hidden />
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body
        )}

      {sortMenuOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="hist-mt5-sheet-menu-overlay"
            role="presentation"
            onClick={() => setSortMenuOpen(false)}
          >
            <div
              className="hist-mt5-sheet-menu hist-mt5-sheet-menu--sort"
              role="menu"
              aria-label={t("sortBy")}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="hist-mt5-sheet-menu__head">
                <span className="hist-mt5-sheet-menu__title">{t("sortBy")}</span>
                <button
                  type="button"
                  className="hist-mt5-sheet-menu__close"
                  aria-label={t("close")}
                  onClick={() => setSortMenuOpen(false)}
                >
                  ✕
                </button>
              </div>
              {HISTORY_SORTS.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  role="menuitemradio"
                  aria-checked={sortId === s.id}
                  className={`hist-mt5-sheet-menu__item${sortId === s.id ? " is-active" : ""}`}
                  onClick={() => {
                    setSortId(s.id);
                    setSortMenuOpen(false);
                  }}
                >
                  <span className="hist-mt5-sheet-menu__label">{t(s.labelKey)}</span>
                  {sortId === s.id ? (
                    <span className="hist-mt5-sheet-menu__radio" aria-hidden />
                  ) : null}
                </button>
              ))}
            </div>
          </div>,
          document.body
        )}

      {isMobileMt5Page && customStripVisible && periodId === "custom"
        ? createPortal(
            <div className="hist-mt5-custom-strip" role="group" aria-label="Custom period">
              <button
                type="button"
                className={`hist-mt5-custom-strip__date${
                  customOpen && customTextFocus === "start" ? " is-active" : ""
                }`}
                onClick={() => openCustomDateField("start")}
              >
                {formatCustomStripDate(customStart || startDate)}
              </button>
              <span className="hist-mt5-custom-strip__dash" aria-hidden>
                –
              </span>
              <button
                type="button"
                className={`hist-mt5-custom-strip__date${
                  customOpen && customTextFocus === "end" ? " is-active" : ""
                }`}
                onClick={() => openCustomDateField("end")}
              >
                {formatCustomStripDate(customEnd || endDate)}
              </button>
              <button
                type="button"
                className="hist-mt5-custom-strip__close"
                aria-label="Close custom period"
                onClick={() => {
                  setCustomStripVisible(false);
                  setCustomOpen(false);
                  setPeriodMenuOpen(true);
                }}
              >
                ✕
              </button>
            </div>,
            document.body
          )
        : null}

      {customOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="hist-mt5-period-overlay--center"
            role="presentation"
            onClick={closeCustomRangePicker}
          >
            <div
              className="hist-mt5-md-datepicker"
              role="dialog"
              aria-label={customTextFocus === "end" ? "Select to date" : "Select from date"}
              onClick={(e) => e.stopPropagation()}
            >
              {(() => {
                const today = customToday;
                const fromDate = customStart
                  ? moment(customStart).startOf("day").toDate()
                  : null;
                const minDate = customTextFocus === "end" && fromDate ? fromDate : null;
                const maxDate = today;
                let rawActive =
                  customTextFocus === "end"
                    ? customEnd || customStart || today
                    : customStart || customEnd || today;
                let activeMoment = moment(rawActive).startOf("day");
                if (activeMoment.isAfter(moment(maxDate), "day")) activeMoment = moment(maxDate);
                if (minDate && activeMoment.isBefore(moment(minDate), "day")) {
                  activeMoment = moment(minDate);
                }
                const activeDate = activeMoment.toDate();
                const activeText =
                  customTextFocus === "end" ? customEndText : customStartText;
                const yearMin = customTextFocus === "end" && fromDate
                  ? moment(fromDate).year()
                  : 1970;
                const yearMax = moment(maxDate).year();
                const yearList = [];
                for (let y = yearMin; y <= yearMax; y += 1) yearList.push(y);
                const setActiveDate = (d) => {
                  if (!d) return;
                  let next = moment(d).startOf("day");
                  if (next.isAfter(moment(maxDate), "day")) next = moment(maxDate);
                  if (customTextFocus === "end" && fromDate && next.isBefore(moment(fromDate), "day")) {
                    next = moment(fromDate);
                  }
                  const nextDate = next.toDate();
                  const text = next.format("DD/MM/YYYY");
                  if (customTextFocus === "end") {
                    setCustomEnd(nextDate);
                    setCustomEndText(text);
                  } else {
                    setCustomStart(nextDate);
                    setCustomStartText(text);
                    // If From moves past To, bump To forward to match
                    if (!customEnd || next.isAfter(moment(customEnd), "day")) {
                      setCustomEnd(nextDate);
                      setCustomEndText(text);
                    }
                  }
                };
                const pickYear = (year) => {
                  const clampedYear = Math.min(Math.max(year, yearMin), yearMax);
                  let next = moment(activeDate).clone().year(clampedYear);
                  if (next.isAfter(moment(maxDate), "day")) next = moment(maxDate);
                  if (minDate && next.isBefore(moment(minDate), "day")) next = moment(minDate);
                  setActiveDate(next.toDate());
                  setCustomYearPickerOpen(false);
                };
                return (
                  <>
                    <div className="hist-mt5-md-datepicker__title-bar">
                      <div className="hist-mt5-md-datepicker__title-text">
                        <div className="hist-mt5-md-datepicker__select-label">Select date</div>
                        <div className="hist-mt5-md-datepicker__selected-date">
                          {moment(activeDate).format("D MMM YYYY")}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="hist-mt5-md-datepicker__edit"
                        aria-label={customPickerTextMode ? "Show calendar" : "Edit date"}
                        onClick={() => {
                          if (!customPickerTextMode) {
                            setActiveDate(activeDate);
                            setCustomYearPickerOpen(false);
                          } else {
                            const formats = [
                              "DD/MM/YYYY",
                              "MM/DD/YYYY",
                              "YYYY-MM-DD",
                              "MM-DD-YYYY",
                              "DD-MM-YYYY",
                            ];
                            const parsed = moment(activeText.trim(), formats, true);
                            if (parsed.isValid()) setActiveDate(parsed.toDate());
                          }
                          setCustomPickerTextMode((v) => !v);
                        }}
                      >
                        {customPickerTextMode ? (
                          <svg
                            className="hist-mt5-md-datepicker__edit-icon"
                            viewBox="0 0 24 24"
                            width="22"
                            height="22"
                            aria-hidden
                          >
                            <path
                              fill="currentColor"
                              d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2z"
                            />
                          </svg>
                        ) : (
                          <svg
                            className="hist-mt5-md-datepicker__edit-icon"
                            viewBox="0 0 24 24"
                            width="22"
                            height="22"
                            aria-hidden
                          >
                            <path
                              fill="currentColor"
                              d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
                            />
                          </svg>
                        )}
                      </button>
                    </div>
                    <div className="hist-mt5-md-datepicker__toolbar">
                      {!customPickerTextMode ? (
                        customYearPickerOpen ? (
                          <div className="hist-mt5-md-datepicker__year-panel">
                            <div className="hist-mt5-md-datepicker__header">
                              <button
                                type="button"
                                className="hist-mt5-md-datepicker__month-label is-open"
                                onClick={() => setCustomYearPickerOpen(false)}
                              >
                                {moment(activeDate).format("MMMM YYYY")}
                                <span className="hist-mt5-md-datepicker__caret" aria-hidden>
                                  ▴
                                </span>
                              </button>
                            </div>
                            <div
                              className="hist-mt5-md-datepicker__year-grid"
                              role="listbox"
                              aria-label="Select year"
                              ref={(el) => {
                                if (!el) return;
                                const selected = el.querySelector(
                                  ".hist-mt5-md-datepicker__year.is-selected"
                                );
                                if (selected && typeof selected.scrollIntoView === "function") {
                                  selected.scrollIntoView({ block: "center" });
                                }
                              }}
                            >
                              {yearList.map((year) => (
                                <button
                                  key={year}
                                  type="button"
                                  role="option"
                                  aria-selected={year === moment(activeDate).year()}
                                  className={`hist-mt5-md-datepicker__year${
                                    year === moment(activeDate).year() ? " is-selected" : ""
                                  }`}
                                  onClick={() => pickYear(year)}
                                >
                                  {year}
                                </button>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <DatePicker
                            selected={activeDate}
                            onChange={(date) => setActiveDate(date)}
                            inline
                            minDate={minDate || undefined}
                            maxDate={maxDate}
                            openToDate={activeDate}
                            calendarClassName="hist-mt5-md-datepicker__cal"
                            renderCustomHeader={({
                              date,
                              decreaseMonth,
                              increaseMonth,
                              prevMonthButtonDisabled,
                              nextMonthButtonDisabled,
                            }) => {
                              const view = moment(date);
                              const atOrPastMaxMonth = view.isSameOrAfter(
                                moment(maxDate),
                                "month"
                              );
                              const atOrBeforeMinMonth = minDate
                                ? view.isSameOrBefore(moment(minDate), "month")
                                : false;
                              return (
                                <div className="hist-mt5-md-datepicker__header">
                                  <button
                                    type="button"
                                    className="hist-mt5-md-datepicker__month-label"
                                    onClick={() => setCustomYearPickerOpen(true)}
                                  >
                                    {view.format("MMMM YYYY")}
                                    <span className="hist-mt5-md-datepicker__caret" aria-hidden>
                                      ▾
                                    </span>
                                  </button>
                                  <div className="hist-mt5-md-datepicker__arrows">
                                    <button
                                      type="button"
                                      className="hist-mt5-md-datepicker__arrow"
                                      onClick={decreaseMonth}
                                      disabled={
                                        prevMonthButtonDisabled || atOrBeforeMinMonth
                                      }
                                      aria-label="Previous month"
                                    >
                                      ‹
                                    </button>
                                    <button
                                      type="button"
                                      className="hist-mt5-md-datepicker__arrow"
                                      onClick={increaseMonth}
                                      disabled={
                                        nextMonthButtonDisabled || atOrPastMaxMonth
                                      }
                                      aria-label="Next month"
                                    >
                                      ›
                                    </button>
                                  </div>
                                </div>
                              );
                            }}
                          />
                        )
                      ) : (
                        <div className="hist-mt5-md-datepicker__text-wrap">
                          <label className="hist-mt5-md-outlined is-focused">
                            <span className="hist-mt5-md-outlined__label">
                              {customTextFocus === "end" ? "To date" : "From date"}
                            </span>
                            <input
                              className="hist-mt5-md-outlined__input"
                              value={activeText}
                              onChange={(e) => {
                                if (customTextFocus === "end") setCustomEndText(e.target.value);
                                else setCustomStartText(e.target.value);
                              }}
                              inputMode="numeric"
                              placeholder="DD/MM/YYYY"
                              autoFocus
                            />
                          </label>
                        </div>
                      )}
                    </div>
                    <div className="hist-mt5-md-datepicker__actions">
                      <button type="button" onClick={closeCustomRangePicker}>
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="is-primary"
                        disabled={
                          customPickerTextMode
                            ? !String(activeText || "").trim()
                            : !activeDate
                        }
                        onClick={confirmCustomRangePicker}
                      >
                        OK
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
